/**
 * Comprehensive Numerology Archetype Library
 * 
 * Deep archetypal meanings, connections, and relationships for all numbers.
 * Based on extensive research from multiple numerology traditions.
 * 
 * Sources:
 * - Pythagorean Numerology (ancient Greek)
 * - Chaldean Numerology (ancient Babylonian)
 * - Kabbalistic Numerology (Hebrew mysticism)
 * - Chinese Numerology traditions
 * - Modern numerology interpretations (Decoz, Millman, McCants)
 */

// ============================================================================
// ARCHETYPE TYPES
// ============================================================================

export interface CulturalArchetypeMapping {
  tradition: string;
  meaning: string;
  symbolism: string[];
  associations?: string[];
  luckyStatus?: 'lucky' | 'unlucky' | 'neutral' | 'sacred';
  notes?: string;
}

export interface NumberArchetype {
  number: number;
  isMaster: boolean;
  
  // Core Identity
  title: string;
  essence: string;
  keywords: string[];
  
  // Archetypal Meanings
  coreMeaning: string;
  symbolicMeaning: string;
  spiritualMeaning: string;
  
  // Personality & Traits
  strengths: string[];
  challenges: string[];
  talents: string[];
  shadowAspects: string[];
  
  // Life Themes
  lifePurpose: string;
  lifeLessons: string[];
  karmicDebts?: string[];
  
  // Relationships
  compatibleNumbers: number[];
  challengingNumbers: number[];
  relationshipDynamics: Record<number, string>;
  
  // Career & Expression
  careerPaths: string[];
  vocationalCalling: string;
  
  // Sacred Connections
  color: string;
  element: string;
  planet?: string;
  astrologicalCorrespondence?: string;
  chakra?: string;
  frequency: number;
  
  // Symbolic Associations
  symbols: string[];
  shapeDefinitions?: Record<string, {
    geometricForm: string;
    description: string;
    significance: string;
    practicalApplication?: string;
    dailyLifeExamples?: string[];
    howToWorkWithIt?: string[];
  }>;
  mythologicalFigures?: string[];
  tarotCorrespondence?: string;
  gemstones?: string[];
  flowers?: string[];
  
  // Cycles & Timing
  favorableMonths?: number[];
  favorableDays?: number[];
  peakYears?: number[];
  
  // Master Number Specific
  masterNumberDescription?: string;
  manifestationLevel?: 'Physical' | 'Emotional' | 'Mental' | 'Spiritual';
  
  // Cultural Archetype Mappings
  culturalArchetypes?: CulturalArchetypeMapping[];
}

// ============================================================================
// COMPREHENSIVE ARCHETYPE LIBRARY
// ============================================================================

export const NUMBER_ARCHETYPES: Record<number, NumberArchetype> = {
  // NOTE: 0 is treated as a special meta-archetype in many traditions.
  // Here we include it explicitly so every digit 0–9 has a defined archetype.
  0: {
    number: 0,
    isMaster: false,
    title: 'The Void',
    essence: 'Chaos, truth, infinite potential, pre-creation field',
    keywords: ['Chaos', 'Truth', 'Zero Point', 'Potential', 'Emptiness', 'Source'],

    coreMeaning: 'The number of the void and zero point. Represents chaos as fertile ground and the unformed truth before manifestation.',
    symbolicMeaning: 'The empty circle, portal, and womb of creation. The pause before the first movement, the mystery behind all numbers.',
    spiritualMeaning: 'Pure being without form. Radical truth that dissolves illusion and returns awareness to the source.',

    strengths: ['Radically Honest', 'Disruptive in a Sacred Way', 'Able to Start from Nothing', 'Comfortable with the Unknown'],
    challenges: ['Destabilizing', 'Hard to Grasp', 'Can Feel Like Nothingness', 'Difficult Relationship with Linear Progress'],
    talents: ['Pattern Breaking', 'Resetting Cycles', 'Holding Space for the Unknown', 'Revealing Illusion'],
    shadowAspects: ['Nihilism', 'Total Collapse', 'Endless Limbo', 'Refusal to Incarnate or Commit'],

    lifePurpose: 'Represents the zero point of creation. Embodies sacred chaos that clears what is false so new truth can emerge.',
    lifeLessons: [
      'Learning to trust the void between cycles',
      'Letting go of false structures so deeper truth can appear',
      'Finding meaning without clinging to form',
      'Remembering that emptiness is full of potential',
      'Recognizing chaos as a teacher, not an enemy'
    ],

    compatibleNumbers: [1, 2, 8, 9],
    challengingNumbers: [4, 6],
    relationshipDynamics: {
      0: 'Two voids amplify dissolution of what is false and open a portal for radical new beginnings',
      1: 'Chaos clears the way so new light and initiative can arise',
      2: 'The void invites deeper unity beyond polarity, but may unsettle harmony',
      3: 'Dreams and expression are stripped to their essence, then rebuilt from truth',
      4: 'Structure may feel threatened by chaos, yet can be rebuilt on truer foundations',
      5: 'Change becomes quantum, not linear – sudden resets and breakthroughs',
      6: 'Attachment to harmony is challenged so more honest love can emerge',
      7: 'Inner awakening is accelerated by confronting the void directly',
      8: 'Success is purified of ego when built from zero instead of scarcity',
      9: 'Completion becomes total release back into the cosmic field'
    },

    careerPaths: [
      'Change Agent', 'Shadow Worker', 'Systems Disruptor', 'Mystic',
      'Crisis Counselor', 'Artist of the Void', 'Researcher of First Principles'
    ],
    vocationalCalling: 'Represents radical reset, truth-telling, and returning systems to the zero point so they can be reborn.',

    color: 'Black with Iridescent Hints',
    element: 'Aether',
    planet: 'Pluto',
    astrologicalCorrespondence: 'Scorpio, Aquarius',
    chakra: 'Void Between Chakras (Zero Point Field)',
    frequency: 0,

    symbols: ['Empty Circle', 'Portal', 'Black Sun', 'Abyss', 'Seed in the Dark'],
    shapeDefinitions: {
      'Empty Circle': {
        geometricForm: 'Circle (0 vertices, infinite radius)',
        description: 'A perfect circle with no content, representing the void before manifestation. The geometric form of zero - no angles, no edges, pure potential.',
        significance: 'Represents infinite potential, the womb of creation, and the unmanifested state. The circle has no beginning or end, symbolizing eternity and the void that contains all possibilities.'
      },
      'Portal': {
        geometricForm: 'Circular opening (2D) or torus (3D)',
        description: 'A circular gateway or opening that connects different realms or dimensions. The geometric form of transition and transformation.',
        significance: 'Represents the threshold between states, the gateway of transformation, and the point of entry/exit between manifest and unmanifest realms. The portal shape emphasizes the circular nature of zero as a connector.'
      },
      'Black Sun': {
        geometricForm: 'Circle with dark center (concentric circles)',
        description: 'A circular form representing the sun in its void state - the dark sun that exists before light. Multiple concentric circles creating depth.',
        significance: 'Represents the source of all light hidden in darkness, the void that contains all energy, and the unmanifested power that precedes creation. The circular form emphasizes the cyclical nature of creation and destruction.'
      },
      'Abyss': {
        geometricForm: 'Infinite depth (vertical circle or spiral)',
        description: 'A bottomless circular or spiral form representing infinite depth. The geometric expression of the void that has no boundaries.',
        significance: 'Represents the infinite depth of the void, the endless potential that cannot be measured, and the abyss that both terrifies and liberates. The circular/spiral form shows the infinite nature of zero.'
      },
      'Seed in the Dark': {
        geometricForm: 'Small circle within larger circle (concentric)',
        description: 'A small circular form (seed) contained within a larger circular form (darkness). Two concentric circles representing potential within the void.',
        significance: 'Represents the seed of all creation hidden in the void, the potential that exists before manifestation, and the promise of life within emptiness. The concentric circles show how zero contains all numbers within itself.'
      }
    },
    mythologicalFigures: ['Kali', 'Ereshkigal', 'The Great Void', 'Primordial Night'],
    tarotCorrespondence: 'The Fool (pre-step), The Void between cards',
    gemstones: ['Obsidian', 'Black Tourmaline', 'Onyx', 'Black Moonstone'],
    flowers: ['Night-Blooming Flowers', 'Black Rose'],

    favorableMonths: [10],
    favorableDays: [],
    peakYears: [],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Normalcy, simplicity, absence of complexities. The unmanifested state before creation. Zero represents the void before form, the empty circle of potential.',
        symbolism: ['Empty circle', 'Womb', 'Potential', 'Beginning and end', 'Void', 'Source'],
        associations: ['Yin principle', 'Receptivity', 'The void before form', 'Tao', 'Wu wei'],
        luckyStatus: 'neutral',
        notes: 'In Mandarin, zero implies normalcy and simplicity, denoting absence of complexities. The void from which all things emerge in Taoist philosophy.'
      },
      {
        tradition: 'Buddhist',
        meaning: 'Śūnyatā (emptiness) - not absence but infinite potential. The pregnant void from which all phenomena arise. The ground of being before manifestation.',
        symbolism: ['Empty circle', 'Fullness in emptiness', 'Formless source', 'Void', 'Potential'],
        associations: ['Nirvana', 'Enlightenment', 'Non-duality', 'Śūnyatā', 'Dharmakaya', 'Buddha nature'],
        luckyStatus: 'sacred',
        notes: 'The void is not emptiness as absence but emptiness as infinite potential. The ultimate reality beyond all concepts and forms.'
      },
      {
        tradition: 'Hindu',
        meaning: 'Brahman - the formless absolute, the source of all creation. The unmanifested reality. The zero point from which all numbers and forms emerge.',
        symbolism: ['Om', 'The uncreated', 'Absolute consciousness', 'Void', 'Source'],
        associations: ['Brahman', 'Atman', 'Maya', 'Nirguna Brahman', 'Para Brahman', 'The Absolute'],
        luckyStatus: 'sacred',
        notes: 'Represents the ultimate reality beyond all forms and names. The unmanifested source from which all manifestation arises.'
      },
      {
        tradition: 'Mayan',
        meaning: 'The void, the beginning of all cycles. The space between worlds. The zero day that marks the end and beginning of cycles.',
        symbolism: ['Empty shell', 'The underworld', 'Between realms', 'Void', 'Transformation'],
        associations: ['Xibalba', 'The void', 'Transformation', 'Zero day', 'Cycle completion'],
        luckyStatus: 'neutral',
        notes: 'Represents the space between cycles and the transformation realm. The void that precedes all new beginnings.'
      },
      {
        tradition: 'Native American',
        meaning: 'The Great Mystery, the unmanifested. The void from which all things emerge. The formless source that gives birth to all forms.',
        symbolism: ['Empty circle', 'The void', 'Source', 'Great Mystery', 'Origin'],
        associations: ['Great Spirit', 'The Mystery', 'Origin', 'Wakan Tanka', 'The Void'],
        luckyStatus: 'sacred',
        notes: 'Represents the formless source of all creation. The Great Mystery that cannot be named but from which all things arise.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Al-Fard (The One) in its unmanifested state. The void before the first creation. The absolute unity before differentiation.',
        symbolism: ['Empty circle', 'The uncreated', 'Source', 'Void', 'Unity'],
        associations: ['Tawhid', 'The void', 'Origin', 'Al-Ahad', 'The Unmanifested'],
        luckyStatus: 'sacred',
        notes: 'Represents the unmanifested state of the Divine before creation. The absolute unity before the first differentiation.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Ein Sof (The Infinite) - the endless, formless source. The void before emanation. The infinite nothingness that contains all potential.',
        symbolism: ['Empty circle', 'The infinite', 'Unmanifested light', 'Void', 'Ein Sof'],
        associations: ['Ein Sof', 'Ain', 'The void', 'Ain Sof', 'The Infinite', 'Before Keter'],
        luckyStatus: 'sacred',
        notes: 'Represents the infinite, formless source before the first emanation. The void that precedes the Tree of Life.'
      },
      {
        tradition: 'Celtic',
        meaning: 'The void, the cauldron of creation. The space between worlds where transformation occurs. The empty vessel that holds infinite potential.',
        symbolism: ['Empty cauldron', 'The void', 'Between worlds', 'Void', 'Transformation'],
        associations: ['The Otherworld', 'Transformation', 'The void', 'Cauldron of Cerridwen', 'Between realms'],
        luckyStatus: 'neutral',
        notes: 'Represents the transformative void between the worlds. The empty cauldron from which all things are reborn.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'Nun - the primordial waters, the unformed chaos before creation. The void from which Atum emerged. The infinite potential before manifestation.',
        symbolism: ['Primordial waters', 'Nun', 'Void', 'Chaos', 'Potential'],
        associations: ['Nun', 'The primordial', 'Chaos', 'Before Atum', 'The unformed'],
        luckyStatus: 'sacred',
        notes: 'Represents Nun, the primordial waters of chaos from which all creation emerged. The void before the first god.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'Chaos - the void, the gap, the unformed state before cosmos. The infinite potential before order. The primordial state before the gods.',
        symbolism: ['Chaos', 'Void', 'Gap', 'The unformed', 'Potential'],
        associations: ['Chaos', 'The void', 'Before cosmos', 'Primordial state', 'The gap'],
        luckyStatus: 'neutral',
        notes: 'Represents Chaos, the primordial void from which all things emerged. The unformed state before the ordering of cosmos.'
      },
      {
        tradition: 'Norse',
        meaning: 'Ginnungagap - the great void, the yawning gap between fire and ice. The empty space from which all creation emerged.',
        symbolism: ['Ginnungagap', 'The void', 'The gap', 'Empty space', 'Potential'],
        associations: ['Ginnungagap', 'The void', 'Before creation', 'The gap', 'Primordial space'],
        luckyStatus: 'neutral',
        notes: 'Represents Ginnungagap, the great void between Muspelheim and Niflheim from which all creation emerged.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'Shunyata - emptiness as the true nature of reality. The void that is full of potential. The ground of being before manifestation.',
        symbolism: ['Shunyata', 'Void', 'Emptiness', 'Potential', 'Ground'],
        associations: ['Shunyata', 'Emptiness', 'Dharmata', 'The void', 'True nature'],
        luckyStatus: 'sacred',
        notes: 'Represents shunyata, the emptiness that is the true nature of all phenomena. The void that contains infinite potential.'
      },
      {
        tradition: 'African',
        meaning: 'The void, the unmanifested source. The empty space before creation. The potential from which all things emerge.',
        symbolism: ['Void', 'Empty space', 'Source', 'Potential', 'The unformed'],
        associations: ['The void', 'Source', 'Before creation', 'Potential', 'The unmanifested'],
        luckyStatus: 'neutral',
        notes: 'Represents the void before creation, the empty space that holds infinite potential for all things.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The Dreaming before form. The void from which all stories emerge. The unmanifested potential of creation.',
        symbolism: ['Dreaming', 'Void', 'Stories', 'Potential', 'The unformed'],
        associations: ['Dreamtime', 'The void', 'Before form', 'Stories', 'Potential'],
        luckyStatus: 'sacred',
        notes: 'Represents the Dreaming before manifestation, the void from which all creation stories and forms emerge.'
      },
      {
        tradition: 'Korean',
        meaning: 'The void, emptiness, the unmanifested state. The empty circle of potential before form. The source before creation.',
        symbolism: ['Void', 'Emptiness', 'Empty circle', 'Potential', 'Source'],
        associations: ['The void', 'Emptiness', 'Before form', 'Potential', 'Source'],
        luckyStatus: 'neutral',
        notes: 'Represents the void and emptiness, the unmanifested state before all forms and numbers emerge.'
      }
    ]
  },

  1: {
    number: 1,
    isMaster: false,
    title: 'Unity',
    essence: 'Leadership, independence, originality, new beginnings',
    keywords: ['Leadership', 'Independence', 'Innovation', 'Courage', 'Self-Reliance', 'Initiative'],
    
    coreMeaning: 'The number of beginnings, leadership, and the self. Represents the masculine principle, the active force, and the spark of creation.',
    symbolicMeaning: 'The point from which all things emerge. The first step, the origin, the monad. Represents individuality and the assertion of will.',
    spiritualMeaning: 'The spark of divine consciousness asserting itself. The "I AM" principle. The journey from unity to individuation.',
    
    strengths: ['Independent', 'Original', 'Courageous', 'Self-Reliant', 'Ambitious', 'Determined', 'Innovative', 'Pioneering'],
    challenges: ['Egotistical', 'Stubborn', 'Impatient', 'Arrogant', 'Domineering', 'Self-Centered', 'Loner Tendency'],
    talents: ['Leadership', 'Invention', 'Innovation', 'Entrepreneurship', 'Public Speaking', 'Taking Initiative'],
    shadowAspects: ['Tyranny', 'Isolation', 'Selfishness', 'Arrogance', 'Ruthlessness'],
    
    lifePurpose: 'Represents leadership, innovation, and new beginnings. Embodies self-reliance and courage in forging new paths.',
    lifeLessons: [
      'Learning to lead without dominating',
      'Balancing independence with cooperation',
      'Developing patience and humility',
      'Sharing credit and empowering others',
      'Understanding that true leadership serves'
    ],
    
    compatibleNumbers: [3, 5, 7],
    challengingNumbers: [2, 4, 6],
    relationshipDynamics: {
      1: 'Two independent forces can clash or create powerful synergy',
      2: 'Needs cooperation but may resist dependency',
      3: 'Creative partnership with mutual inspiration',
      4: 'Stability vs. freedom - requires compromise',
      5: 'Adventure and excitement, but may lack grounding',
      6: 'Family harmony may conflict with independence',
      7: 'Spiritual depth and introspection complement leadership',
      8: 'Power struggles possible, but potential for great achievement',
      9: 'Humanitarian service may challenge individual focus'
    },
    
    careerPaths: [
      'Entrepreneur', 'CEO', 'Inventor', 'Pioneer in any field',
      'Politician', 'Manager', 'Director', 'Engineer',
      'Architect', 'Scientist', 'Explorer', 'Athlete'
    ],
    vocationalCalling: 'Represents initiation, leadership, and the creation of new structures and systems.',
    
    color: 'Red',
    element: 'Fire',
    planet: 'Sun',
    astrologicalCorrespondence: 'Aries, Leo',
    chakra: 'Root (Muladhara)',
    frequency: 396, // Liberation frequency
    
    symbols: ['Point', 'Stick', 'Lance', 'Sword', 'Tower', 'Monolith'],
    shapeDefinitions: {
      'Point': {
        geometricForm: 'Point (0 dimensions, no size)',
        description: 'A single point with no dimensions - the most fundamental geometric form. The origin from which all lines, shapes, and forms emerge.',
        significance: 'Represents the beginning, the origin, the first manifestation from zero. The point is the geometric expression of unity - one location, one purpose, one direction. It is the seed of all linear forms.'
      },
      'Stick': {
        geometricForm: 'Line segment (1D, 2 vertices)',
        description: 'A straight line segment - the first dimension. Two points connected, creating direction and purpose. The simplest linear form.',
        significance: 'Represents the first step, direction, and purpose. The stick is the geometric expression of will and intention - a single direction, unwavering focus. It shows the power of one to create structure.'
      },
      'Lance': {
        geometricForm: 'Line with pointed end (1D with direction)',
        description: 'A linear form with a pointed end, emphasizing direction and penetration. A line that moves forward with purpose.',
        significance: 'Represents forward movement, penetration, and the ability to break through barriers. The lance shape shows the power of one to initiate action and create change through focused direction.'
      },
      'Sword': {
        geometricForm: 'Double-edged line (1D with symmetry)',
        description: 'A linear form with two edges, representing balance in action. A line that cuts both ways, showing the power and responsibility of leadership.',
        significance: 'Represents the dual nature of power - it can create or destroy. The sword shape shows that leadership (one) carries both the ability to protect and the responsibility to use power wisely. The linear form emphasizes direct action.'
      },
      'Tower': {
        geometricForm: 'Vertical line/rectangle (1D extended upward)',
        description: 'A vertical linear form extending upward, representing aspiration and achievement. A line that reaches toward the heavens.',
        significance: 'Represents ambition, achievement, and the upward striving of leadership. The tower shape shows the power of one to build structures and reach great heights. The vertical line emphasizes growth and elevation.'
      },
      'Monolith': {
        geometricForm: 'Rectangular prism (3D, 8 vertices, 12 edges)',
        description: 'A three-dimensional rectangular form - the solid expression of one. A structure that stands alone, independent and complete.',
        significance: 'Represents independence, self-sufficiency, and the complete manifestation of unity. The monolith shape shows how one creates solid, lasting structures. The rectangular form emphasizes stability and foundation built from the power of one.'
      }
    },
    mythologicalFigures: ['Zeus', 'Odin', 'Shiva (as creator)', 'Adam (first man)'],
    tarotCorrespondence: 'The Magician',
    gemstones: ['Ruby', 'Garnet', 'Red Jasper', 'Carnelian'],
    flowers: ['Red Rose', 'Poppy', 'Tulip', 'Carnation'],
    
    favorableMonths: [1, 4, 7, 10],
    favorableDays: [1, 10, 19, 28],
    peakYears: [19, 28, 37, 46, 55, 64],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Superiority, excellence, primacy. The first, the beginning, the leader. Represents yang energy, the active principle, and the origin of all things.',
        symbolism: ['First', 'Beginning', 'Unity', 'The one', 'Yang', 'Heaven'],
        associations: ['Yang principle', 'Masculine', 'Active', 'Heaven', 'Primacy', 'Excellence'],
        luckyStatus: 'lucky',
        notes: 'Represents primacy and excellence in sequence. Associated with leadership and superiority. The yang principle of active creation.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Ekam (The One) - unity, the source, Brahman as the singular reality. The first principle. The indivisible one from which all diversity emerges.',
        symbolism: ['Unity', 'The one', 'Source', 'Beginning', 'Brahman', 'Atman'],
        associations: ['Brahman', 'Atman', 'The first', 'Unity', 'Ekam', 'The source'],
        luckyStatus: 'sacred',
        notes: 'Represents the unity underlying all diversity, the one source. The indivisible reality that appears as many.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Al-Ahad (The One) - the singular, unique God. The first principle of unity. The absolute oneness that cannot be divided.',
        symbolism: ['Unity', 'The one', 'Unique', 'First', 'Tawhid', 'Oneness'],
        associations: ['Tawhid', 'Unity', 'The first', 'Unique', 'Al-Ahad', 'The One'],
        luckyStatus: 'sacred',
        notes: 'Represents the absolute unity and uniqueness of the Divine. The one God who is indivisible and unique.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Aleph (א) - the first letter, the ox, strength, leadership. The beginning of all things. The first emanation from Ein Sof.',
        symbolism: ['Aleph', 'The ox', 'Strength', 'Beginning', 'First letter', 'Unity'],
        associations: ['Aleph', 'Keter (Crown)', 'The first', 'Unity', 'Ox', 'Strength'],
        luckyStatus: 'sacred',
        notes: 'Aleph represents the first emanation, the beginning of creation. The ox symbolizes strength and the power to begin.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Ichi (一) - the first, unity, the beginning. Simplicity and primacy. The single stroke that contains all potential.',
        symbolism: ['One line', 'Unity', 'Beginning', 'Simplicity', 'First', 'The one'],
        associations: ['First', 'Unity', 'Beginning', 'Simplicity', 'Ichi', 'The one'],
        luckyStatus: 'lucky',
        notes: 'Represents the first, unity, and the beginning of all things. The simplicity of the single line contains infinite complexity.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Hun (One) - the first, unity, the beginning of cycles. The first day. The origin point of all temporal cycles.',
        symbolism: ['First', 'Unity', 'Beginning', 'The first day', 'Hun', 'Origin'],
        associations: ['Hun', 'The first', 'Unity', 'Beginning', 'First day', 'Cycle start'],
        luckyStatus: 'lucky',
        notes: 'Represents the first day and the beginning of all cycles. The unity from which all temporal patterns emerge.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Aon (One) - unity, the first, the beginning. The singular source. The one that contains all potential before division.',
        symbolism: ['Unity', 'The first', 'Beginning', 'The one', 'Aon', 'Source'],
        associations: ['First', 'Unity', 'Beginning', 'Source', 'Aon', 'The one'],
        luckyStatus: 'lucky',
        notes: 'Represents the first, unity, and the beginning of all things. The singular source before all division.'
      },
      {
        tradition: 'Native American',
        meaning: 'The first, unity, the beginning. The singular source of all creation. The one spirit that manifests as all things.',
        symbolism: ['Unity', 'The first', 'Beginning', 'The one', 'Source', 'Spirit'],
        associations: ['First', 'Unity', 'Beginning', 'Source', 'Great Spirit', 'The one'],
        luckyStatus: 'lucky',
        notes: 'Represents the first principle and the unity underlying all things. The one Great Spirit in all forms.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'Atum - the first god, the self-created one. The origin point of all creation. The one who emerged from Nun to begin all things.',
        symbolism: ['Atum', 'The first', 'Self-created', 'Origin', 'Unity', 'Beginning'],
        associations: ['Atum', 'The first god', 'Self-creation', 'Origin', 'Unity', 'The one'],
        luckyStatus: 'sacred',
        notes: 'Represents Atum, the first god who self-created from Nun. The origin point of all Egyptian creation.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Monad - the first principle, the indivisible one. The origin of all numbers. The unity from which all multiplicity emerges.',
        symbolism: ['Monad', 'The one', 'Unity', 'First principle', 'Origin', 'Indivisible'],
        associations: ['Monad', 'The one', 'Unity', 'First principle', 'Pythagorean', 'Origin'],
        luckyStatus: 'sacred',
        notes: 'Represents the Monad, the first principle in Pythagorean philosophy. The indivisible one from which all numbers emerge.'
      },
      {
        tradition: 'Norse',
        meaning: 'The first, the origin. The one that stands alone. The beginning of all things in the Norse cosmology.',
        symbolism: ['The first', 'Origin', 'Unity', 'Beginning', 'The one', 'Alone'],
        associations: ['The first', 'Origin', 'Unity', 'Beginning', 'The one', 'First principle'],
        luckyStatus: 'lucky',
        notes: 'Represents the first principle, the origin point of all things in Norse cosmology. The one that stands alone.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The one, unity, the first principle. The indivisible reality. The source from which all phenomena arise.',
        symbolism: ['Unity', 'The one', 'First', 'Indivisible', 'Source', 'Reality'],
        associations: ['The one', 'Unity', 'First principle', 'Indivisible', 'Source', 'Reality'],
        luckyStatus: 'sacred',
        notes: 'Represents the one, the indivisible reality from which all phenomena arise. The unity underlying all diversity.'
      },
      {
        tradition: 'African',
        meaning: 'The first, unity, the beginning. The one source from which all things emerge. The origin point of creation.',
        symbolism: ['The first', 'Unity', 'Beginning', 'Source', 'The one', 'Origin'],
        associations: ['The first', 'Unity', 'Beginning', 'Source', 'The one', 'Origin'],
        luckyStatus: 'lucky',
        notes: 'Represents the first, the unity, and the beginning. The one source from which all creation emerges.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The first, the one, unity. The beginning of all stories. The singular source from which all forms emerge.',
        symbolism: ['The first', 'Unity', 'The one', 'Beginning', 'Stories', 'Source'],
        associations: ['The first', 'Unity', 'The one', 'Beginning', 'Stories', 'Source'],
        luckyStatus: 'sacred',
        notes: 'Represents the first, the one, and unity. The beginning of all creation stories and the source of all forms.'
      },
      {
        tradition: 'Korean',
        meaning: 'The first, unity, the beginning. The one that contains all potential. The origin point of all things.',
        symbolism: ['The first', 'Unity', 'Beginning', 'The one', 'Origin', 'Potential'],
        associations: ['The first', 'Unity', 'Beginning', 'The one', 'Origin', 'Potential'],
        luckyStatus: 'lucky',
        notes: 'Represents the first, unity, and the beginning. The one that contains all potential before manifestation.'
      }
    ]
  },
  
  2: {
    number: 2,
    isMaster: false,
    title: 'Duality',
    essence: 'Cooperation, harmony, partnership, sensitivity, intuition',
    keywords: ['Cooperation', 'Balance', 'Diplomacy', 'Intuition', 'Patience', 'Sensitivity'],
    
    coreMeaning: 'The number of partnership, cooperation, and balance. Represents the feminine principle, receptivity, and the power of two working together.',
    symbolicMeaning: 'The first division, duality, yin and yang. The balance between opposites. The container that holds potential.',
    spiritualMeaning: 'The divine feminine, receptive consciousness. The capacity to feel, sense, and harmonize. The bridge between worlds.',
    
    strengths: ['Cooperative', 'Intuitive', 'Diplomatic', 'Patient', 'Sensitive', 'Harmonious', 'Supportive', 'Tactful'],
    challenges: ['Overly Sensitive', 'Indecisive', 'Dependent', 'Passive', 'Moody', 'Submissive', 'Self-Doubt'],
    talents: ['Mediation', 'Counseling', 'Artistic Expression', 'Healing', 'Teaching', 'Service'],
    shadowAspects: ['Codependency', 'Victimhood', 'Manipulation', 'Indecision', 'Passivity'],
    
    lifePurpose: 'Represents harmony, cooperation, and the bridging of concepts. Embodies partnership and balance between opposing forces.',
    lifeLessons: [
      'Learning to balance giving with receiving',
      'Developing independence within partnerships',
      'Trusting intuition while maintaining boundaries',
      'Speaking up for needs and desires',
      'Finding strength in sensitivity'
    ],
    
    compatibleNumbers: [4, 6, 8],
    challengingNumbers: [1, 5, 7],
    relationshipDynamics: {
      1: 'Completes the leader, but may feel overshadowed',
      2: 'Deep understanding and harmony, but risk of codependency',
      3: 'Creative harmony with emotional sensitivity',
      4: 'Stable foundation, security, and mutual support',
      5: 'Excitement but may feel unstable',
      6: 'Perfect harmony in home and family',
      7: 'Mystical connection, but may feel distant',
      8: 'Powerful partnership in business, needs emotional balance',
      9: 'Compassionate service together'
    },
    
    careerPaths: [
      'Mediator', 'Counselor', 'Therapist', 'Diplomat',
      'Artist', 'Designer', 'Musician', 'Writer',
      'Nurse', 'Teacher', 'Assistant', 'Coordinator'
    ],
    vocationalCalling: 'Represents harmonization, support, and beauty through cooperation.',
    
    color: 'Orange',
    element: 'Water',
    planet: 'Moon',
    astrologicalCorrespondence: 'Cancer, Libra',
    chakra: 'Sacral (Svadhisthana)',
    frequency: 417, // Change and transformation
    
    symbols: ['Dual Lines', 'Yin Yang', 'Moon', 'Pair', 'Balance Scale', 'Bridge'],
    shapeDefinitions: {
      'Dual Lines': {
        geometricForm: 'Two parallel lines (2 parallel 1D forms)',
        description: 'Two parallel lines running side by side, representing partnership and balance. The geometric expression of duality working in harmony.',
        significance: 'Represents partnership, cooperation, and the balance between two forces. The parallel lines show how two can work together without conflict, maintaining their individual identity while creating harmony. The linear form emphasizes the complementary nature of duality.'
      },
      'Yin Yang': {
        geometricForm: 'Circle divided by S-curve (2 equal areas)',
        description: 'A circle divided into two equal but opposite areas by an S-curve. The perfect geometric expression of balance and interdependence.',
        significance: 'Represents the dynamic balance of opposites, showing how two forces create wholeness. The circular form contains both halves, emphasizing that duality exists within unity. The S-curve shows the flowing, dynamic nature of balance.'
      },
      'Moon': {
        geometricForm: 'Crescent or full circle (curved form)',
        description: 'A curved or circular form representing the moon - the feminine principle. The geometric expression of receptivity and cycles.',
        significance: 'Represents receptivity, intuition, and the cyclical nature of life. The curved/circular form emphasizes the feminine principle of two - receiving, containing, and reflecting. The shape shows the power of receptivity.'
      },
      'Pair': {
        geometricForm: 'Two identical forms (symmetrical pair)',
        description: 'Two identical geometric forms placed together, representing partnership and mirroring. The geometric expression of two working as one.',
        significance: 'Represents partnership, cooperation, and the power of two. The symmetrical pair shows how two identical forces can work together in perfect harmony. The form emphasizes balance and equality in partnership.'
      },
      'Balance Scale': {
        geometricForm: 'Horizontal line with two equal weights (symmetrical)',
        description: 'A horizontal line (beam) with two equal forms (weights) on either side. The perfect geometric expression of balance and justice.',
        significance: 'Represents balance, justice, and the equal weight of opposites. The symmetrical form shows how two forces must be balanced for harmony. The horizontal line emphasizes stability and equilibrium.'
      },
      'Bridge': {
        geometricForm: 'Curved or straight line connecting two points',
        description: 'A linear or curved form connecting two separate points or areas. The geometric expression of connection and unity.',
        significance: 'Represents connection, communication, and the ability to bridge differences. The bridge shape shows how two (duality) creates connection and unity. The form emphasizes the power of two to create harmony and understanding.'
      }
    },
    mythologicalFigures: ['Isis', 'Hera', 'Persephone', 'Lilith'],
    tarotCorrespondence: 'The High Priestess',
    gemstones: ['Moonstone', 'Pearl', 'Opal', 'Larimar'],
    flowers: ['White Lily', 'Jasmine', 'Gardenia', 'Lotus'],
    
    favorableMonths: [2, 5, 8, 11],
    favorableDays: [2, 11, 20, 29],
    peakYears: [20, 29, 38, 47, 56, 65],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Harmony, balance, duality. Yin and yang in perfect balance. Partnership and cooperation. The complementary forces that create wholeness.',
        symbolism: ['Yin yang', 'Duality', 'Balance', 'Pair', 'Harmony', 'Complementary'],
        associations: ['Yin yang', 'Balance', 'Harmony', 'Partnership', 'Complementary forces', 'Wholeness'],
        luckyStatus: 'lucky',
        notes: 'Represents harmony and balance, mirroring Eastern dualistic philosophies. The perfect balance of yin and yang creates harmony.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Dvandva (Duality) - the pair, balance, the two principles. Purusha and Prakriti. The fundamental duality of consciousness and matter.',
        symbolism: ['Duality', 'Pair', 'Balance', 'Two principles', 'Purusha/Prakriti', 'Dvandva'],
        associations: ['Purusha/Prakriti', 'Duality', 'Balance', 'Pair', 'Dvandva', 'Two principles'],
        luckyStatus: 'lucky',
        notes: 'Represents the fundamental duality of consciousness and matter. Purusha (consciousness) and Prakriti (matter) in balance.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Ithnain (Two) - duality, balance, partnership. The pair in harmony. The complementary forces that create unity.',
        symbolism: ['Duality', 'Pair', 'Balance', 'Partnership', 'Harmony', 'Complementary'],
        associations: ['Duality', 'Balance', 'Partnership', 'Pair', 'Harmony', 'Complementary'],
        luckyStatus: 'lucky',
        notes: 'Represents duality, balance, and harmonious partnership. The pair that creates unity through complementarity.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Bet (ב) - the second letter, house, container. The first division, duality. The container that holds the first emanation.',
        symbolism: ['Bet', 'House', 'Container', 'Duality', 'First division', 'Wisdom'],
        associations: ['Bet', 'Chokmah (Wisdom)', 'Duality', 'First division', 'House', 'Container'],
        luckyStatus: 'lucky',
        notes: 'Bet represents the first division and the container of creation. The house that holds the wisdom of duality.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Ni (二) - two, duality, balance. The pair in harmony. The two lines that create stability and partnership.',
        symbolism: ['Two lines', 'Duality', 'Balance', 'Pair', 'Harmony', 'Stability'],
        associations: ['Two', 'Duality', 'Balance', 'Pair', 'Harmony', 'Stability'],
        luckyStatus: 'lucky',
        notes: 'Represents duality, balance, and harmonious partnership. The two lines that create stability through balance.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Ka (Two) - duality, balance, the second day. The pair principle. The complementary forces that create cycles.',
        symbolism: ['Two', 'Duality', 'Balance', 'The second day', 'Ka', 'Pair'],
        associations: ['Ka', 'Two', 'Duality', 'Balance', 'Second day', 'Pair principle'],
        luckyStatus: 'lucky',
        notes: 'Represents the second day and the principle of duality. The pair that creates balance in all cycles.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Dó (Two) - duality, balance, partnership. The pair in harmony. The complementary forces that create wholeness.',
        symbolism: ['Duality', 'Pair', 'Balance', 'Partnership', 'Harmony', 'Complementary'],
        associations: ['Two', 'Duality', 'Balance', 'Pair', 'Harmony', 'Complementary'],
        luckyStatus: 'lucky',
        notes: 'Represents duality, balance, and harmonious partnership. The pair that creates wholeness through complementarity.'
      },
      {
        tradition: 'Native American',
        meaning: 'Two - duality, balance, partnership. The pair principle in harmony. The complementary forces that create unity.',
        symbolism: ['Duality', 'Pair', 'Balance', 'Partnership', 'Harmony', 'Complementary'],
        associations: ['Two', 'Duality', 'Balance', 'Pair', 'Harmony', 'Complementary'],
        luckyStatus: 'lucky',
        notes: 'Represents the fundamental duality and balance in all things. The pair that creates unity through harmony.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The pair, duality, balance. The complementary forces of creation. The two lands (Upper and Lower Egypt) united.',
        symbolism: ['Pair', 'Duality', 'Balance', 'Two lands', 'Complementary', 'Unity'],
        associations: ['Two lands', 'Duality', 'Balance', 'Pair', 'Upper/Lower Egypt', 'Unity'],
        luckyStatus: 'lucky',
        notes: 'Represents the pair, duality, and balance. The two lands of Egypt united in harmony.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Dyad - the first division, duality, the pair. The principle of opposition and balance. The two that creates all relationship.',
        symbolism: ['Dyad', 'Duality', 'Pair', 'Division', 'Balance', 'Opposition'],
        associations: ['Dyad', 'Duality', 'Pair', 'First division', 'Balance', 'Opposition'],
        luckyStatus: 'lucky',
        notes: 'Represents the Dyad, the first division in Pythagorean philosophy. The two that creates all relationship and balance.'
      },
      {
        tradition: 'Norse',
        meaning: 'The pair, duality, balance. The complementary forces in Norse cosmology. The two that creates harmony.',
        symbolism: ['Pair', 'Duality', 'Balance', 'Complementary', 'Harmony', 'Two'],
        associations: ['Pair', 'Duality', 'Balance', 'Complementary', 'Harmony', 'Two'],
        luckyStatus: 'lucky',
        notes: 'Represents the pair, duality, and balance. The complementary forces that create harmony in Norse cosmology.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The two, duality, balance. The pair principle. The complementary forces that create wholeness in Tibetan philosophy.',
        symbolism: ['Two', 'Duality', 'Balance', 'Pair', 'Complementary', 'Wholeness'],
        associations: ['Two', 'Duality', 'Balance', 'Pair', 'Complementary', 'Wholeness'],
        luckyStatus: 'lucky',
        notes: 'Represents the two, duality, and balance. The pair that creates wholeness through complementary forces.'
      },
      {
        tradition: 'African',
        meaning: 'The pair, duality, balance. The complementary forces of creation. The two that creates harmony and wholeness.',
        symbolism: ['Pair', 'Duality', 'Balance', 'Complementary', 'Harmony', 'Wholeness'],
        associations: ['Pair', 'Duality', 'Balance', 'Complementary', 'Harmony', 'Wholeness'],
        luckyStatus: 'lucky',
        notes: 'Represents the pair, duality, and balance. The complementary forces that create harmony and wholeness.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The two, the pair, duality. The complementary forces in creation stories. The pair that creates balance and harmony.',
        symbolism: ['Two', 'Pair', 'Duality', 'Balance', 'Complementary', 'Harmony'],
        associations: ['Two', 'Pair', 'Duality', 'Balance', 'Complementary', 'Harmony'],
        luckyStatus: 'lucky',
        notes: 'Represents the two, the pair, and duality. The complementary forces in creation stories that create balance.'
      },
      {
        tradition: 'Korean',
        meaning: 'The two, duality, balance. The pair in harmony. The complementary forces that create unity and wholeness.',
        symbolism: ['Two', 'Duality', 'Balance', 'Pair', 'Harmony', 'Unity'],
        associations: ['Two', 'Duality', 'Balance', 'Pair', 'Harmony', 'Unity'],
        luckyStatus: 'lucky',
        notes: 'Represents the two, duality, and balance. The pair that creates unity and wholeness through harmony.'
      }
    ]
  },
  
  3: {
    number: 3,
    isMaster: false,
    title: 'Trinity',
    essence: 'Creativity, self-expression, joy, communication, optimism',
    keywords: ['Creativity', 'Expression', 'Joy', 'Communication', 'Optimism', 'Artistic'],
    
    coreMeaning: 'The number of creativity, self-expression, and the power of the word. Represents the trinity, birth of form, and the joy of creation.',
    symbolicMeaning: 'The triangle, the first geometric form. The trinity of body, mind, spirit. The creative force in action.',
    spiritualMeaning: 'The creative expression of the divine. The power of manifestation through words, art, and joy. The dance of life.',
    
    strengths: ['Creative', 'Expressive', 'Optimistic', 'Joyful', 'Communicative', 'Inspiring', 'Charismatic', 'Artistic'],
    challenges: ['Scattered', 'Superficial', 'Gossipy', 'Self-Indulgent', 'Inconsistent', 'Scatterbrained', 'Overly Social'],
    talents: ['Art', 'Writing', 'Speaking', 'Performance', 'Design', 'Marketing', 'Teaching'],
    shadowAspects: ['Superficiality', 'Scatter', 'Gossip', 'Excess', 'Drama'],
    
    lifePurpose: 'Represents creativity, expression, and communication. Embodies the joy of creation and the power of words.',
    lifeLessons: [
      'Focusing creative energy for manifestation',
      'Using words and expression responsibly',
      'Balancing social life with inner work',
      'Developing depth alongside breadth',
      'Finding joy without losing substance'
    ],
    
    compatibleNumbers: [1, 5, 6, 9],
    challengingNumbers: [4, 7, 8],
    relationshipDynamics: {
      1: 'Creative inspiration and dynamic partnership',
      2: 'Beautiful harmony with emotional sensitivity',
      3: 'Triple creativity, but needs grounding',
      4: 'Structure needed for scattered creativity',
      5: 'Explosive creativity and adventure',
      6: 'Creative home and family expression',
      7: 'Mystical creativity, but may feel misunderstood',
      8: 'Creative business success possible',
      9: 'Creative humanitarian expression'
    },
    
    careerPaths: [
      'Artist', 'Writer', 'Performer', 'Designer',
      'Communicator', 'Teacher', 'Marketer', 'Entertainer',
      'Chef', 'Architect', 'Fashion Designer', 'Musician'
    ],
    vocationalCalling: 'Represents creation, inspiration, and expression of beauty and truth through art and communication.',
    
    color: 'Yellow',
    element: 'Fire',
    planet: 'Jupiter',
    astrologicalCorrespondence: 'Sagittarius, Gemini',
    chakra: 'Solar Plexus (Manipura)',
    frequency: 528, // Transformation and love frequency
    
    symbols: ['Triangle', 'Trinity', 'Pyramid', 'Eye', 'Lotus', 'Trident'],
    shapeDefinitions: {
      'Triangle': {
        geometricForm: 'Triangle (3 vertices, 3 edges, first stable polygon)',
        description: 'A three-sided polygon - the first stable geometric form. Three points connected, creating the first closed shape that cannot collapse.',
        significance: 'Represents stability, foundation, and the first manifestation of structure. The triangle is the geometric expression of three - the first form that stands on its own. The three sides show how three creates stability and foundation.'
      },
      'Trinity': {
        geometricForm: 'Three interlocking circles or three-part form',
        description: 'A geometric form with three equal parts - three circles, three points, or three connected elements. The perfect expression of three working as one.',
        significance: 'Represents the trinity principle - three aspects creating wholeness. The form shows how three forces (body/mind/spirit, past/present/future, etc.) work together. The interlocking nature emphasizes unity in diversity.'
      },
      'Pyramid': {
        geometricForm: 'Tetrahedron or square pyramid (4 triangular faces, 4 vertices)',
        description: 'A three-dimensional form with triangular faces - the solid expression of three. A structure that rises from a base to a point.',
        significance: 'Represents the upward expression of three, showing how creativity and expression build upward. The pyramid shape shows how three creates structures that reach toward the divine. The triangular faces emphasize the trinity principle in three dimensions.'
      },
      'Eye': {
        geometricForm: 'Circle with triangle (eye of providence) or almond shape',
        description: 'A circular or almond-shaped form, often with triangular elements. The geometric expression of vision, insight, and perception.',
        significance: 'Represents vision, insight, and the power of three to see clearly. The eye shape shows how three (creativity) brings clarity and understanding. The form emphasizes the ability to see patterns and express truth.'
      },
      'Lotus': {
        geometricForm: 'Radial symmetry with 3 or multiple of 3 petals',
        description: 'A circular form with radial symmetry, typically with three or multiples of three petals. The geometric expression of beauty and spiritual growth.',
        significance: 'Represents beauty, spiritual growth, and the creative expression of three. The lotus shape shows how three creates beauty and harmony. The radial symmetry emphasizes the outward expression of creativity.'
      },
      'Trident': {
        geometricForm: 'Three-pronged form (three lines from one point)',
        description: 'A form with three prongs or points extending from a central base. The geometric expression of three directions or three powers.',
        significance: 'Represents the three-fold power of three - the ability to act in three directions simultaneously. The trident shape shows how three creates power and influence. The three prongs emphasize the creative force of three.'
      }
    },
    mythologicalFigures: ['Brahma (Creator)', 'Thoth (Scribe)', 'Muses', 'Saraswati'],
    tarotCorrespondence: 'The Empress',
    gemstones: ['Citrine', 'Amber', 'Topaz', 'Yellow Sapphire'],
    flowers: ['Sunflower', 'Marigold', 'Daffodil', 'Buttercup'],
    
    favorableMonths: [3, 6, 9, 12],
    favorableDays: [3, 12, 21, 30],
    peakYears: [21, 30, 39, 48, 57, 66],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Fortitude, stability, abundance. Considered very lucky. The trinity principle of Heaven, Earth, and Humanity. The first stable form.',
        symbolism: ['Trinity', 'Three', 'Stability', 'Abundance', 'Triangle', 'Heaven/Earth/Humanity'],
        associations: ['Heaven, Earth, Humanity', 'Trinity', 'Lucky number', 'Stability', 'Abundance', 'Fortitude'],
        luckyStatus: 'lucky',
        notes: 'Signifies fortitude, stability, and abundance. Deemed very lucky in Chinese culture. The trinity of Heaven, Earth, and Humanity.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Trimurti (Trinity) - Brahma, Vishnu, Shiva. The three gunas. Creation, preservation, destruction. The three aspects of the divine.',
        symbolism: ['Trinity', 'Three', 'Trimurti', 'Three gunas', 'Triangle', 'Three aspects'],
        associations: ['Brahma/Vishnu/Shiva', 'Sattva/Rajas/Tamas', 'Trinity', 'Trimurti', 'Three gunas', 'Creation/Preservation/Destruction'],
        luckyStatus: 'sacred',
        notes: 'Represents the trinity of creation, preservation, and destruction. The three gunas (qualities) of nature.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Thalatha (Three) - trinity, stability, the three principles. The first stable form. The triangle that creates foundation.',
        symbolism: ['Trinity', 'Three', 'Stability', 'Triangle', 'Three principles', 'Foundation'],
        associations: ['Trinity', 'Stability', 'Three principles', 'Triangle', 'First stable form', 'Foundation'],
        luckyStatus: 'lucky',
        notes: 'Represents the trinity and the first stable geometric form. The triangle that creates stability and foundation.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Gimel (ג) - the third letter, camel, bridge. The trinity principle. The bridge between worlds that creates understanding.',
        symbolism: ['Gimel', 'Camel', 'Bridge', 'Trinity', 'Three', 'Understanding'],
        associations: ['Gimel', 'Binah (Understanding)', 'Trinity', 'Three', 'Camel', 'Bridge'],
        luckyStatus: 'sacred',
        notes: 'Represents the trinity and the bridge between worlds. Gimel as the camel that bridges understanding.'
      },
      {
        tradition: 'Japanese',
        meaning: 'San (三) - three, trinity, stability. The first stable form. The three lines that create the triangle of stability.',
        symbolism: ['Three lines', 'Trinity', 'Stability', 'Triangle', 'San', 'First stable'],
        associations: ['Three', 'Trinity', 'Stability', 'Triangle', 'San', 'First stable form'],
        luckyStatus: 'lucky',
        notes: 'Represents the trinity and stability. The three lines that create the first stable geometric form.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Ox (Three) - trinity, stability, the third day. The first stable form. The trinity that creates the foundation of cycles.',
        symbolism: ['Three', 'Trinity', 'Stability', 'The third day', 'Ox', 'Triangle'],
        associations: ['Ox', 'Three', 'Trinity', 'Stability', 'Third day', 'First stable'],
        luckyStatus: 'lucky',
        notes: 'Represents the third day and the principle of stability. The trinity that creates the foundation of all cycles.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Trí (Three) - trinity, the three realms. Land, sea, sky. The sacred three that creates the world.',
        symbolism: ['Trinity', 'Three', 'Three realms', 'Sacred', 'Land/Sea/Sky', 'Triangle'],
        associations: ['Land/Sea/Sky', 'Trinity', 'Sacred three', 'Three realms', 'Trí', 'World creation'],
        luckyStatus: 'sacred',
        notes: 'Represents the trinity of land, sea, and sky - the three realms. The sacred three that creates the world.'
      },
      {
        tradition: 'Native American',
        meaning: 'Three - trinity, stability, the three worlds. Upper, middle, lower worlds. The sacred trinity of existence.',
        symbolism: ['Trinity', 'Three', 'Three worlds', 'Stability', 'Upper/Middle/Lower', 'Triangle'],
        associations: ['Upper/Middle/Lower worlds', 'Trinity', 'Three', 'Three worlds', 'Stability', 'Sacred'],
        luckyStatus: 'sacred',
        notes: 'Represents the trinity of the three worlds. The sacred trinity that creates the structure of existence.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The trinity, the three. The first stable form. The triangle that creates the foundation. The three aspects of the divine.',
        symbolism: ['Trinity', 'Three', 'Triangle', 'Stability', 'Three aspects', 'Foundation'],
        associations: ['Trinity', 'Three', 'Triangle', 'Stability', 'Three aspects', 'Foundation'],
        luckyStatus: 'sacred',
        notes: 'Represents the trinity and the first stable form. The triangle that creates the foundation of Egyptian cosmology.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Triad - the trinity, the first stable form. The triangle that creates structure. The three principles of existence.',
        symbolism: ['Triad', 'Trinity', 'Three', 'Triangle', 'Stability', 'Three principles'],
        associations: ['Triad', 'Trinity', 'Three', 'Triangle', 'Stability', 'Three principles'],
        luckyStatus: 'sacred',
        notes: 'Represents the Triad, the trinity in Pythagorean philosophy. The first stable form that creates structure.'
      },
      {
        tradition: 'Norse',
        meaning: 'The three, trinity, stability. The first stable form. The three realms or three aspects that create structure.',
        symbolism: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three realms', 'First stable'],
        associations: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three realms', 'First stable'],
        luckyStatus: 'lucky',
        notes: 'Represents the three, trinity, and stability. The first stable form that creates structure in Norse cosmology.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The three, trinity, stability. The first stable form. The three aspects of reality that create understanding.',
        symbolism: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three aspects', 'Understanding'],
        associations: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three aspects', 'Understanding'],
        luckyStatus: 'sacred',
        notes: 'Represents the three, trinity, and stability. The first stable form that creates understanding in Tibetan philosophy.'
      },
      {
        tradition: 'African',
        meaning: 'The three, trinity, stability. The first stable form. The three aspects that create the foundation of existence.',
        symbolism: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three aspects', 'Foundation'],
        associations: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three aspects', 'Foundation'],
        luckyStatus: 'lucky',
        notes: 'Represents the three, trinity, and stability. The first stable form that creates the foundation of existence.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The three, trinity, stability. The first stable form. The three aspects in creation stories that create structure.',
        symbolism: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three aspects', 'Stories'],
        associations: ['Three', 'Trinity', 'Stability', 'Triangle', 'Three aspects', 'Stories'],
        luckyStatus: 'sacred',
        notes: 'Represents the three, trinity, and stability. The first stable form in creation stories that creates structure.'
      },
      {
        tradition: 'Korean',
        meaning: 'The three, trinity, stability. The first stable form. The triangle that creates foundation and abundance.',
        symbolism: ['Three', 'Trinity', 'Stability', 'Triangle', 'Foundation', 'Abundance'],
        associations: ['Three', 'Trinity', 'Stability', 'Triangle', 'Foundation', 'Abundance'],
        luckyStatus: 'lucky',
        notes: 'Represents the three, trinity, and stability. The first stable form that creates foundation and abundance.'
      }
    ]
  },
  
  4: {
    number: 4,
    isMaster: false,
    title: 'Square',
    essence: 'Stability, structure, foundation, discipline, practicality',
    keywords: ['Stability', 'Structure', 'Foundation', 'Discipline', 'Practical', 'Reliable'],
    
    coreMeaning: 'The number of foundation, structure, and material reality. Represents the four elements, the four directions, and the building blocks of life.',
    symbolicMeaning: 'The square, the cube, the four corners. Stability, order, and the manifestation of form. The material plane.',
    spiritualMeaning: 'Building the temple of the self through discipline and structure. Grounding spirit in matter. Practical spirituality.',
    
    strengths: ['Stable', 'Practical', 'Reliable', 'Disciplined', 'Organized', 'Hardworking', 'Loyal', 'Systematic'],
    challenges: ['Rigid', 'Stubborn', 'Limiting', 'Workaholic', 'Controlling', 'Inflexible', 'Boring'],
    talents: ['Building', 'Engineering', 'Organization', 'Management', 'Finance', 'Construction'],
    shadowAspects: ['Rigidity', 'Control', 'Limitation', 'Workaholism', 'Stubbornness'],
    
    lifePurpose: 'Represents stability, structure, and foundation. Embodies practical manifestation and the building blocks of form.',
    lifeLessons: [
      'Learning flexibility within structure',
      'Balancing work with play',
      'Understanding that rules serve growth',
      'Building on solid foundations',
      'Releasing control and rigidity'
    ],
    
    compatibleNumbers: [2, 6, 8],
    challengingNumbers: [1, 3, 5],
    relationshipDynamics: {
      1: 'Provides structure for leadership, but may feel controlled',
      2: 'Stable foundation and security',
      3: 'Creativity needs structure, but may feel constrained',
      4: 'Doubled stability, but risk of rigidity',
      5: 'Freedom vs. structure conflict',
      6: 'Perfect for building home and family',
      7: 'Structure supports spiritual practice',
      8: 'Powerful material manifestation together',
      9: 'Structure for humanitarian service'
    },
    
    careerPaths: [
      'Architect', 'Engineer', 'Builder', 'Manager',
      'Accountant', 'Banker', 'Contractor', 'Farmer',
      'Organizer', 'Administrator', 'Military', 'Security'
    ],
    vocationalCalling: 'Represents building, organization, and the creation of lasting structures in the material world.',
    
    color: 'Green',
    element: 'Earth',
    planet: 'Uranus',
    astrologicalCorrespondence: 'Taurus, Capricorn',
    chakra: 'Heart (Anahata)',
    frequency: 639, // Connection and relationships
    
    symbols: ['Square', 'Cube', 'Foundation', 'Pillar', 'Four Corners', 'Altar'],
    shapeDefinitions: {
      'Square': {
        geometricForm: 'Square (4 vertices, 4 equal sides, 4 right angles)',
        description: 'A four-sided polygon with equal sides and right angles - the perfect expression of stability and order. The geometric foundation of the material world.',
        significance: 'Represents stability, foundation, and the material plane. The square is the geometric expression of four - the first form that creates solid, reliable structure. The four equal sides show how four creates perfect balance and order.',
        practicalApplication: 'The Square is your foundation builder. It shows you how to create stability in your life through structure, routine, and practical systems. Like a building foundation, the Square teaches you to establish solid ground before building upward. When life feels unstable, the Square reminds you to strengthen your four cornerstones: physical health, emotional security, mental clarity, and practical resources.',
        dailyLifeExamples: [
          'Creating a daily routine with 4 core pillars: morning ritual, work blocks, family time, and evening reflection',
          'Building financial stability through 4 foundations: income, savings, investments, and emergency fund',
          'Establishing a home that feels secure through 4 elements: safety, comfort, organization, and beauty',
          'Building relationships on 4 cornerstones: trust, respect, communication, and commitment',
          'Organizing your workspace with clear boundaries and structured systems'
        ],
        howToWorkWithIt: [
          'When feeling unstable, identify the 4 cornerstones of your life and strengthen the weakest one',
          'Use the Square principle in planning: break goals into 4 steps or 4 time periods',
          'Create square structures in your environment: organize items in square patterns, use square containers, arrange furniture in square formations',
          'Practice the Square meditation: visualize your life as a square with 4 equal sides, ensuring each corner is strong',
          'When overwhelmed, use the Square method: identify 4 priorities, focus on one corner at a time, build systematically'
        ]
      },
      'Cube': {
        geometricForm: 'Cube (8 vertices, 12 edges, 6 square faces)',
        description: 'A three-dimensional form with six square faces - the solid expression of four. A structure that is stable in all directions.',
        significance: 'Represents solid foundation, material reality, and the complete manifestation of four. The cube shape shows how four creates structures that are stable in all three dimensions. The six faces emphasize the comprehensive nature of four\'s foundation.'
      },
      'Foundation': {
        geometricForm: 'Square base or rectangular platform',
        description: 'A square or rectangular base that supports structures above. The geometric expression of the foundation principle.',
        significance: 'Represents the foundation upon which all things are built. The square/rectangular form shows how four creates the stable base for all growth and development. The shape emphasizes the importance of solid foundations.'
      },
      'Pillar': {
        geometricForm: 'Vertical rectangular prism (square cross-section)',
        description: 'A vertical form with square cross-section - the linear expression of four. A structure that supports and elevates.',
        significance: 'Represents support, structure, and the ability of four to create lasting foundations. The pillar shape shows how four creates structures that support and elevate. The square cross-section emphasizes the stability of four.'
      },
      'Four Corners': {
        geometricForm: 'Square with emphasis on four corner points',
        description: 'A square form emphasizing the four corner points - the cardinal directions. The geometric expression of four directions.',
        significance: 'Represents the four directions, four elements, and the comprehensive nature of four. The four corners show how four creates complete coverage of space. The form emphasizes the all-encompassing stability of four.'
      },
      'Altar': {
        geometricForm: 'Square or rectangular platform (raised base)',
        description: 'A square or rectangular raised platform - the sacred expression of four. A structure that elevates and sanctifies.',
        significance: 'Represents sacred foundation, ritual structure, and the spiritual expression of four. The altar shape shows how four creates structures for sacred purposes. The square/rectangular form emphasizes the stability needed for spiritual practice.'
      }
    },
    mythologicalFigures: ['Atlas', 'Vulcan', 'Hephaestus', 'Vishwakarma'],
    tarotCorrespondence: 'The Emperor',
    gemstones: ['Emerald', 'Jade', 'Green Tourmaline', 'Peridot'],
    flowers: ['Ivy', 'Oak', 'Cedar', 'Pine'],
    
    favorableMonths: [4, 7, 10, 1],
    favorableDays: [4, 13, 22, 31],
    peakYears: [22, 31, 40, 49, 58, 67],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Pronounced similarly to "death" (sì), making it unlucky. Avoided in many contexts. However, represents the four directions and foundation.',
        symbolism: ['Four', 'Square', 'Foundation', 'Death', 'Four directions', 'Square'],
        associations: ['Four directions', 'Square', 'Foundation', 'Death sound', 'Unlucky', 'Avoided'],
        luckyStatus: 'unlucky',
        notes: 'Pronounced similarly to "death" (sì), making it an unlucky number to be avoided. However, still represents the four directions and foundation.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Chatur (Four) - the four directions, the four Vedas, stability, foundation. The four goals of life (Purusharthas).',
        symbolism: ['Four', 'Square', 'Four directions', 'Foundation', 'Four Vedas', 'Stability'],
        associations: ['Four Vedas', 'Four directions', 'Square', 'Stability', 'Four Purusharthas', 'Foundation'],
        luckyStatus: 'lucky',
        notes: 'Represents the four directions, four Vedas, and stable foundation. The four goals of human life: Dharma, Artha, Kama, Moksha.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Arba\'a (Four) - the four elements, the four directions, stability, foundation. The four righteous caliphs.',
        symbolism: ['Four', 'Square', 'Four elements', 'Foundation', 'Four directions', 'Stability'],
        associations: ['Four elements', 'Four directions', 'Square', 'Stability', 'Four caliphs', 'Foundation'],
        luckyStatus: 'lucky',
        notes: 'Represents the four elements and stable foundation. The four directions that create order and structure.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Dalet (ד) - the fourth letter, door, four. The four directions, foundation. The door to manifestation.',
        symbolism: ['Dalet', 'Door', 'Four', 'Foundation', 'Four directions', 'Square'],
        associations: ['Dalet', 'Chesed (Mercy)', 'Four directions', 'Foundation', 'Door', 'Manifestation'],
        luckyStatus: 'lucky',
        notes: 'Represents the four directions and the foundation of creation. Dalet as the door to material manifestation.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Shi (四) - four, but also "death" (shi), making it unlucky. Often avoided. However, represents foundation and stability.',
        symbolism: ['Four', 'Square', 'Foundation', 'Death', 'Four directions', 'Stability'],
        associations: ['Four', 'Square', 'Foundation', 'Death sound', 'Unlucky', 'Avoided'],
        luckyStatus: 'unlucky',
        notes: 'Can mean "death" (shi), making it unlucky and often avoided. However, still represents foundation and the four directions.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Kan (Four) - the four directions, foundation, stability. The fourth day. The square that creates the foundation of cycles.',
        symbolism: ['Four', 'Square', 'Four directions', 'The fourth day', 'Kan', 'Foundation'],
        associations: ['Kan', 'Four', 'Four directions', 'Foundation', 'Fourth day', 'Stability'],
        luckyStatus: 'lucky',
        notes: 'Represents the four directions and the foundation of cycles. The square that creates stability in all temporal patterns.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Ceathair (Four) - the four directions, the four elements, foundation, stability. The four quarters of the year.',
        symbolism: ['Four', 'Square', 'Four directions', 'Foundation', 'Four elements', 'Stability'],
        associations: ['Four directions', 'Four elements', 'Square', 'Stability', 'Four quarters', 'Foundation'],
        luckyStatus: 'lucky',
        notes: 'Represents the four directions and stable foundation. The four quarters of the year and the four elements.'
      },
      {
        tradition: 'Native American',
        meaning: 'Four - the four directions, the four elements, foundation, stability. Sacred number. The four winds and four seasons.',
        symbolism: ['Four', 'Square', 'Four directions', 'Foundation', 'Four elements', 'Sacred'],
        associations: ['Four directions', 'Four elements', 'Square', 'Sacred', 'Four winds', 'Four seasons'],
        luckyStatus: 'sacred',
        notes: 'Represents the four directions and is considered sacred. The four winds, four seasons, and four elements create the foundation of existence.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The four, foundation, stability. The four sons of Horus. The four pillars that hold up the sky. The square of foundation.',
        symbolism: ['Four', 'Square', 'Foundation', 'Four sons', 'Four pillars', 'Stability'],
        associations: ['Four sons of Horus', 'Four pillars', 'Square', 'Foundation', 'Stability', 'Four directions'],
        luckyStatus: 'lucky',
        notes: 'Represents the four, foundation, and stability. The four sons of Horus and the four pillars that support the sky.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Tetrad - the four, foundation, stability. The four elements. The square that creates the material world.',
        symbolism: ['Tetrad', 'Four', 'Square', 'Foundation', 'Four elements', 'Stability'],
        associations: ['Tetrad', 'Four', 'Square', 'Foundation', 'Four elements', 'Stability'],
        luckyStatus: 'lucky',
        notes: 'Represents the Tetrad, the four in Pythagorean philosophy. The square that creates the foundation of the material world.'
      },
      {
        tradition: 'Norse',
        meaning: 'The four, foundation, stability. The four directions. The square that creates the foundation of the world.',
        symbolism: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'World'],
        associations: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'World'],
        luckyStatus: 'lucky',
        notes: 'Represents the four, foundation, and stability. The square that creates the foundation of the Norse world.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The four, foundation, stability. The four directions. The square that creates the foundation of understanding.',
        symbolism: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Understanding'],
        associations: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Understanding'],
        luckyStatus: 'lucky',
        notes: 'Represents the four, foundation, and stability. The square that creates the foundation of understanding in Tibetan philosophy.'
      },
      {
        tradition: 'African',
        meaning: 'The four, foundation, stability. The four directions. The square that creates the foundation of existence.',
        symbolism: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Existence'],
        associations: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Existence'],
        luckyStatus: 'lucky',
        notes: 'Represents the four, foundation, and stability. The square that creates the foundation of existence.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The four, foundation, stability. The four directions in creation stories. The square that creates the foundation of the world.',
        symbolism: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Stories'],
        associations: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Stories'],
        luckyStatus: 'sacred',
        notes: 'Represents the four, foundation, and stability. The square that creates the foundation of the world in creation stories.'
      },
      {
        tradition: 'Korean',
        meaning: 'The four, foundation, stability. The four directions. The square that creates foundation and order.',
        symbolism: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Order'],
        associations: ['Four', 'Square', 'Foundation', 'Four directions', 'Stability', 'Order'],
        luckyStatus: 'lucky',
        notes: 'Represents the four, foundation, and stability. The square that creates foundation and order.'
      }
    ]
  },
  
  5: {
    number: 5,
    isMaster: false,
    title: 'Pentagon',
    essence: 'Freedom, adventure, change, versatility, experience',
    keywords: ['Freedom', 'Adventure', 'Change', 'Versatility', 'Experience', 'Curiosity'],
    
    coreMeaning: 'The number of freedom, change, and human experience. Represents the five senses, the five elements, and the quest for experience.',
    symbolicMeaning: 'The pentagram, the five-pointed star. The human form with outstretched arms and legs. Freedom and exploration.',
    spiritualMeaning: 'The soul experiencing the full range of human life. Learning through experience. The adventure of consciousness.',
    
    strengths: ['Adventurous', 'Versatile', 'Freedom-Loving', 'Curious', 'Progressive', 'Resourceful', 'Adaptable', 'Experienced'],
    challenges: ['Restless', 'Impulsive', 'Irresponsible', 'Scattered', 'Addictive', 'Reckless', 'Uncommitted'],
    talents: ['Sales', 'Travel', 'Media', 'Communication', 'Marketing', 'Exploration'],
    shadowAspects: ['Addiction', 'Restlessness', 'Irresponsibility', 'Scatter', 'Recklessness'],
    
    lifePurpose: 'Represents freedom, change, and experience. Embodies versatility, adventure, and the quest for new experiences.',
    lifeLessons: [
      'Finding freedom within commitment',
      'Embracing change without losing self',
      'Using experiences for growth',
      'Balancing adventure with responsibility',
      'Understanding that true freedom includes discipline'
    ],
    
    compatibleNumbers: [1, 3, 7],
    challengingNumbers: [2, 4, 6],
    relationshipDynamics: {
      1: 'Adventure and leadership together',
      2: 'Excitement but may feel unstable',
      3: 'Explosive creativity and fun',
      4: 'Freedom vs. structure conflict',
      5: 'Maximum freedom, but needs grounding',
      6: 'Adventure conflicts with home stability',
      7: 'Spiritual exploration together',
      8: 'Dynamic business partnership',
      9: 'Adventurous humanitarian service'
    },
    
    careerPaths: [
      'Explorer', 'Salesperson', 'Journalist', 'Travel Agent',
      'Photographer', 'Freelancer', 'Media Professional', 'Marketing',
      'Pilot', 'Tour Guide', 'Event Planner', 'Consultant'
    ],
    vocationalCalling: 'Represents experience, exploration, and teaching through adventure and change.',
    
    color: 'Blue',
    element: 'Air',
    planet: 'Mercury',
    astrologicalCorrespondence: 'Gemini, Aquarius',
    chakra: 'Throat (Vishuddha)',
    frequency: 741, // Awakening intuition
    
    symbols: ['Pentagram', 'Five-Pointed Star', 'Hand', 'Compass', 'Wheel'],
    shapeDefinitions: {
      'Pentagram': {
        geometricForm: 'Pentagram (5-pointed star, golden ratio proportions)',
        description: 'A five-pointed star formed by connecting every other vertex of a pentagon. Contains the golden ratio (φ = 1.618) in its proportions.',
        significance: 'Represents the five elements, human experience, and the dynamic balance of five. The pentagram shape shows how five creates harmony through the golden ratio. The five points emphasize the comprehensive nature of human experience.'
      },
      'Five-Pointed Star': {
        geometricForm: 'Star polygon (5 vertices, 5 points)',
        description: 'A star form with five points - the geometric expression of five. A shape that radiates in five directions.',
        significance: 'Represents expansion, freedom, and the outward expression of five. The five-pointed star shows how five creates dynamic movement and change. The radiating points emphasize the freedom and adventure of five.'
      },
      'Hand': {
        geometricForm: 'Five-fingered form (pentadactyl pattern)',
        description: 'A form with five extensions - the biological expression of five. The human hand with five digits.',
        significance: 'Represents human experience, action, and the five senses. The hand shape shows how five connects to the physical body and human experience. The five digits emphasize the comprehensive nature of human perception and action.'
      },
      'Compass': {
        geometricForm: 'Two arms forming variable angles (navigation tool)',
        description: 'A tool with two arms that can form various angles - the geometric expression of five\'s freedom and adaptability.',
        significance: 'Represents direction, freedom, and the ability to navigate change. The compass shape shows how five creates the ability to find direction in freedom. The variable angles emphasize the adaptability of five.'
      },
      'Wheel': {
        geometricForm: 'Circle with 5 or multiple of 5 spokes',
        description: 'A circular form with five or multiples of five spokes - the dynamic expression of five. A structure that moves and changes.',
        significance: 'Represents movement, change, and the cyclical nature of five. The wheel shape shows how five creates dynamic movement and transformation. The circular form with five elements emphasizes the freedom and change inherent in five.'
      }
    },
    mythologicalFigures: ['Hermes', 'Mercury', 'Loki', 'Trickster Gods'],
    tarotCorrespondence: 'The Hierophant',
    gemstones: ['Sapphire', 'Aquamarine', 'Turquoise', 'Blue Topaz'],
    flowers: ['Forget-Me-Not', 'Bluebell', 'Iris', 'Cornflower'],
    
    favorableMonths: [5, 8, 11, 2],
    favorableDays: [5, 14, 23],
    peakYears: [23, 32, 41, 50, 59, 68],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Associated with well-wishes and blessings. Strikes a chord of positivity. The five elements (Wu Xing) that create harmony.',
        symbolism: ['Five', 'Pentagram', 'Five elements', 'Blessings', 'Wu Xing', 'Harmony'],
        associations: ['Five elements', 'Pentagram', 'Blessings', 'Wu Xing', 'Harmony', 'Positivity'],
        luckyStatus: 'lucky',
        notes: 'Associated with well-wishes and blessings, striking a chord of positivity. The five elements (Wu Xing) create harmony and balance.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Pancha (Five) - the five elements, the five senses, the pentagram. Human experience. The five tattvas and five koshas.',
        symbolism: ['Five', 'Pentagram', 'Five elements', 'Five senses', 'Five tattvas', 'Human'],
        associations: ['Five elements', 'Five senses', 'Pentagram', 'Human', 'Five tattvas', 'Five koshas'],
        luckyStatus: 'lucky',
        notes: 'Represents the five elements and the full range of human experience. The five tattvas and five koshas (sheaths) of existence.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Khamsa (Five) - the five pillars, the hand of Fatima, protection, blessings. The five daily prayers.',
        symbolism: ['Five', 'Hand', 'Protection', 'Five pillars', 'Khamsa', 'Blessings'],
        associations: ['Five pillars', 'Hand of Fatima', 'Protection', 'Blessings', 'Five prayers', 'Khamsa'],
        luckyStatus: 'lucky',
        notes: 'Represents the five pillars of Islam and protection through the hand of Fatima. The five daily prayers create spiritual structure.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'He (ה) - the fifth letter, window, breath. The five books of Torah. The breath of life and divine inspiration.',
        symbolism: ['He', 'Window', 'Breath', 'Five', 'Five books', 'Inspiration'],
        associations: ['He', 'Gevurah (Strength)', 'Five books', 'Breath', 'Window', 'Inspiration'],
        luckyStatus: 'lucky',
        notes: 'Represents the five books of Torah and the breath of life. He as the window to divine inspiration and the breath of creation.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Go (五) - five, the five elements, blessings, protection. The pentagram of harmony and balance.',
        symbolism: ['Five', 'Pentagram', 'Five elements', 'Blessings', 'Go', 'Harmony'],
        associations: ['Five', 'Five elements', 'Blessings', 'Pentagram', 'Harmony', 'Protection'],
        luckyStatus: 'lucky',
        notes: 'Represents the five elements and blessings. The pentagram that creates harmony and protection.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Ho (Five) - the five directions (including center), the hand, human experience. The pentagram of the world.',
        symbolism: ['Five', 'Hand', 'Five directions', 'Human', 'Ho', 'Pentagram'],
        associations: ['Ho', 'Five', 'Five directions', 'Hand', 'Human experience', 'Center'],
        luckyStatus: 'lucky',
        notes: 'Represents the five directions (including center) and the human experience. The hand that reaches in all directions.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Cúig (Five) - the five elements, the pentagram, blessings, protection. The five-fold pattern of nature.',
        symbolism: ['Five', 'Pentagram', 'Five elements', 'Blessings', 'Cúig', 'Protection'],
        associations: ['Five', 'Five elements', 'Pentagram', 'Blessings', 'Cúig', 'Five-fold'],
        luckyStatus: 'lucky',
        notes: 'Represents the five elements and blessings. The pentagram that creates the five-fold pattern of nature.'
      },
      {
        tradition: 'Native American',
        meaning: 'Five - the five directions (including center), the hand, human experience, blessings. The sacred pentagram.',
        symbolism: ['Five', 'Hand', 'Five directions', 'Blessings', 'Pentagram', 'Sacred'],
        associations: ['Five directions', 'Hand', 'Human experience', 'Blessings', 'Center', 'Sacred'],
        luckyStatus: 'lucky',
        notes: 'Represents the five directions (including center) and the full range of human experience. The sacred pentagram of existence.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The five, human experience, the five senses. The pentagram of life. The hand that creates and experiences.',
        symbolism: ['Five', 'Pentagram', 'Five senses', 'Hand', 'Human', 'Experience'],
        associations: ['Five', 'Pentagram', 'Five senses', 'Hand', 'Human experience', 'Life'],
        luckyStatus: 'lucky',
        notes: 'Represents the five, human experience, and the five senses. The pentagram that represents the full range of human life.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Pentad - the five, human experience, the pentagram. The five elements and five senses. The golden ratio.',
        symbolism: ['Pentad', 'Five', 'Pentagram', 'Five elements', 'Five senses', 'Golden ratio'],
        associations: ['Pentad', 'Five', 'Pentagram', 'Five elements', 'Five senses', 'Golden ratio'],
        luckyStatus: 'lucky',
        notes: 'Represents the Pentad, the five in Pythagorean philosophy. The pentagram that embodies the golden ratio and human experience.'
      },
      {
        tradition: 'Norse',
        meaning: 'The five, human experience, the pentagram. The five directions and five elements. The hand of creation.',
        symbolism: ['Five', 'Pentagram', 'Five directions', 'Five elements', 'Hand', 'Experience'],
        associations: ['Five', 'Pentagram', 'Five directions', 'Five elements', 'Hand', 'Experience'],
        luckyStatus: 'lucky',
        notes: 'Represents the five, human experience, and the pentagram. The hand that creates and experiences in Norse cosmology.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The five, human experience, the five elements. The pentagram of the five Buddha families. The five wisdoms.',
        symbolism: ['Five', 'Pentagram', 'Five elements', 'Five Buddhas', 'Five wisdoms', 'Experience'],
        associations: ['Five', 'Pentagram', 'Five elements', 'Five Buddhas', 'Five wisdoms', 'Experience'],
        luckyStatus: 'sacred',
        notes: 'Represents the five, human experience, and the five elements. The pentagram of the five Buddha families and five wisdoms.'
      },
      {
        tradition: 'African',
        meaning: 'The five, human experience, the five directions. The pentagram of life. The hand that creates and blesses.',
        symbolism: ['Five', 'Pentagram', 'Five directions', 'Hand', 'Experience', 'Blessings'],
        associations: ['Five', 'Pentagram', 'Five directions', 'Hand', 'Experience', 'Blessings'],
        luckyStatus: 'lucky',
        notes: 'Represents the five, human experience, and the five directions. The pentagram of life and the hand that blesses.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The five, human experience, the five directions. The pentagram in creation stories. The hand of the creator.',
        symbolism: ['Five', 'Pentagram', 'Five directions', 'Hand', 'Experience', 'Stories'],
        associations: ['Five', 'Pentagram', 'Five directions', 'Hand', 'Experience', 'Stories'],
        luckyStatus: 'sacred',
        notes: 'Represents the five, human experience, and the five directions. The pentagram in creation stories and the hand of the creator.'
      },
      {
        tradition: 'Korean',
        meaning: 'The five, human experience, the five elements. The pentagram of harmony. The hand that creates balance.',
        symbolism: ['Five', 'Pentagram', 'Five elements', 'Hand', 'Experience', 'Harmony'],
        associations: ['Five', 'Pentagram', 'Five elements', 'Hand', 'Experience', 'Harmony'],
        luckyStatus: 'lucky',
        notes: 'Represents the five, human experience, and the five elements. The pentagram of harmony and the hand that creates balance.'
      }
    ]
  },
  
  6: {
    number: 6,
    isMaster: false,
    title: 'Hexagon',
    essence: 'Responsibility, nurturing, service, harmony, love',
    keywords: ['Responsibility', 'Nurturing', 'Service', 'Harmony', 'Love', 'Care'],
    
    coreMeaning: 'The number of love, responsibility, and service. Represents the heart, harmony, and the nurturing principle.',
    symbolicMeaning: 'The hexagon, the Star of David, the six-pointed star. Balance between heaven and earth. The heart center.',
    spiritualMeaning: 'Love in action. Service as the highest calling. The heart as the bridge between material and spiritual.',
    
    strengths: ['Loving', 'Responsible', 'Nurturing', 'Harmonious', 'Caring', 'Protective', 'Idealistic', 'Service-Oriented'],
    challenges: ['Overprotective', 'Interfering', 'Perfectionist', 'Self-Righteous', 'Anxious', 'Martyrdom', 'Overgiving'],
    talents: ['Healing', 'Teaching', 'Counseling', 'Parenting', 'Design', 'Music', 'Cooking'],
    shadowAspects: ['Interference', 'Perfectionism', 'Martyrdom', 'Control Through Care'],
    
    lifePurpose: 'Represents nurturing, service, and harmony. Embodies love, responsibility, and the heart-centered principle.',
    lifeLessons: [
      'Learning to give without losing self',
      'Balancing care with boundaries',
      'Serving without martyrdom',
      'Accepting imperfection in self and others',
      'Understanding that true love allows freedom'
    ],
    
    compatibleNumbers: [2, 3, 4, 9],
    challengingNumbers: [1, 5, 7],
    relationshipDynamics: {
      1: 'Independence conflicts with nurturing needs',
      2: 'Perfect harmony in home and family',
      3: 'Creative and loving partnership',
      4: 'Stable foundation for family',
      5: 'Adventure conflicts with home needs',
      6: 'Doubled nurturing, but risk of codependency',
      7: 'Mystical love connection',
      8: 'Material success with loving support',
      9: 'Perfect humanitarian partnership'
    },
    
    careerPaths: [
      'Teacher', 'Nurse', 'Counselor', 'Parent',
      'Chef', 'Interior Designer', 'Musician', 'Artist',
      'Caregiver', 'Social Worker', 'Healer', 'Homemaker'
    ],
    vocationalCalling: 'Represents nurturing, healing, and the creation of beauty through love and service.',
    
    color: 'Indigo',
    element: 'Earth',
    planet: 'Venus',
    astrologicalCorrespondence: 'Virgo, Libra',
    chakra: 'Heart (Anahata)',
    frequency: 852, // Returning to spiritual order
    
    symbols: ['Hexagon', 'Star of David', 'Heart', 'Rose', 'Lotus', 'Home'],
    shapeDefinitions: {
      'Hexagon': {
        geometricForm: 'Hexagon (6 vertices, 6 equal sides, 120° angles)',
        description: 'A six-sided polygon with equal sides - the perfect expression of harmony and balance. The geometric form that creates efficient space-filling.',
        significance: 'Represents harmony, balance, and the perfect expression of six. The hexagon is the geometric expression of six - the form that creates the most efficient use of space. The six equal sides show how six creates perfect harmony and balance.',
        practicalApplication: 'The Hexagon shows you how to create harmony in your daily life. Like a honeycomb, it teaches you to organize your responsibilities, relationships, and commitments in a balanced way where everything supports everything else. When life feels chaotic, the Hexagon reminds you to find the six key areas that need attention and balance them equally.',
        dailyLifeExamples: [
          'Organizing your week into 6 balanced areas: work, family, health, creativity, rest, and service',
          'Creating harmony in your home by balancing the needs of all family members equally',
          'Building a support network where you both give and receive care',
          'Finding the middle ground in conflicts by considering all perspectives',
          'Creating routines that nurture both yourself and others'
        ],
        howToWorkWithIt: [
          'When overwhelmed, identify the 6 main areas of your life and ensure each gets equal attention',
          'Practice the Hexagon meditation: visualize your life as a hexagon with 6 equal sides, checking which side needs strengthening',
          'Use the Hexagon principle in relationships: balance giving and receiving, speaking and listening',
          'Create hexagonal structures in your space: organize items in groups of 6, arrange furniture in hexagonal patterns',
          'When making decisions, consider 6 perspectives: yours, the other person\'s, the practical, the emotional, the long-term, and the spiritual'
        ]
      },
      'Star of David': {
        geometricForm: 'Two interlocking triangles (hexagram, 6 points)',
        description: 'Two interlocking equilateral triangles forming a six-pointed star - the perfect expression of balance between opposites.',
        significance: 'Represents the balance between heaven and earth, spirit and matter. The Star of David shows how six creates harmony through the integration of opposites. The interlocking triangles emphasize the perfect balance of six.'
      },
      'Heart': {
        geometricForm: 'Heart shape (two curves meeting at point, symmetrical)',
        description: 'A symmetrical form with two curves meeting at a point - the geometric expression of love and care.',
        significance: 'Represents love, care, and the emotional expression of six. The heart shape shows how six creates love and harmony. The symmetrical form emphasizes the balance and care inherent in six.'
      },
      'Rose': {
        geometricForm: 'Radial symmetry with 5 or 6 petals (often 6-fold)',
        description: 'A circular form with radial symmetry, typically with five or six petals. The geometric expression of beauty and love.',
        significance: 'Represents beauty, love, and the harmonious expression of six. The rose shape shows how six creates beauty through harmony. The radial symmetry emphasizes the balanced, loving nature of six.'
      },
      'Lotus': {
        geometricForm: 'Radial symmetry with 6 or multiple of 6 petals',
        description: 'A circular form with radial symmetry, typically with six or multiples of six petals. The geometric expression of spiritual beauty.',
        significance: 'Represents spiritual beauty, purity, and the elevated expression of six. The lotus shape shows how six creates spiritual harmony. The radial symmetry emphasizes the balanced, loving, and spiritual nature of six.'
      },
      'Home': {
        geometricForm: 'Hexagonal or square structure with roof (stable base)',
        description: 'A stable structure with a protective roof - the geometric expression of home and family.',
        significance: 'Represents home, family, and the nurturing expression of six. The home shape shows how six creates stable, loving environments. The structure emphasizes the responsibility and care inherent in six.'
      }
    },
    mythologicalFigures: ['Aphrodite', 'Venus', 'Lakshmi', 'Kuan Yin'],
    tarotCorrespondence: 'The Lovers',
    gemstones: ['Rose Quartz', 'Pink Tourmaline', 'Rhodonite', 'Ruby'],
    flowers: ['Rose', 'Peony', 'Carnation', 'Lotus'],
    
    favorableMonths: [6, 9, 12, 3],
    favorableDays: [6, 15, 24],
    peakYears: [24, 33, 42, 51, 60, 69],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'The sound of "six" (liù) resonates with prosperity. Carries connotations of good fortune and wealth. The hexagon of harmony.',
        symbolism: ['Six', 'Hexagon', 'Prosperity', 'Wealth', 'Harmony', 'Good fortune'],
        associations: ['Prosperity', 'Wealth', 'Good fortune', 'Hexagon', 'Harmony', 'Balance'],
        luckyStatus: 'lucky',
        notes: 'The sound of "six" resonates with prosperity, carrying connotations of good fortune and wealth. The hexagon creates harmony and balance.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Shash (Six) - the six directions, the hexagon, balance, harmony. The heart center. The six chakras above the root.',
        symbolism: ['Six', 'Hexagon', 'Six directions', 'Balance', 'Heart', 'Six chakras'],
        associations: ['Six directions', 'Hexagon', 'Balance', 'Heart', 'Six chakras', 'Harmony'],
        luckyStatus: 'lucky',
        notes: 'Represents the six directions and the balance of the heart center. The six chakras above the root create spiritual harmony.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Sitta (Six) - the six days of creation, balance, harmony, the hexagon. The six articles of faith.',
        symbolism: ['Six', 'Hexagon', 'Six days', 'Balance', 'Harmony', 'Six articles'],
        associations: ['Six days of creation', 'Hexagon', 'Balance', 'Harmony', 'Six articles of faith', 'Creation'],
        luckyStatus: 'lucky',
        notes: 'Represents the six days of creation and balance. The six articles of faith create spiritual harmony.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Vav (ו) - the sixth letter, hook, connection. The six directions, balance. Tiferet (Beauty) - the heart center.',
        symbolism: ['Vav', 'Hook', 'Connection', 'Six', 'Six directions', 'Beauty'],
        associations: ['Vav', 'Tiferet (Beauty)', 'Six directions', 'Balance', 'Heart', 'Connection'],
        luckyStatus: 'lucky',
        notes: 'Represents the six directions and the balance of beauty. Vav as the hook that connects and Tiferet as the heart center.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Roku (六) - six, prosperity, wealth, good fortune. The hexagon. The six directions of harmony.',
        symbolism: ['Six', 'Hexagon', 'Prosperity', 'Wealth', 'Good fortune', 'Harmony'],
        associations: ['Six', 'Prosperity', 'Wealth', 'Good fortune', 'Hexagon', 'Harmony'],
        luckyStatus: 'lucky',
        notes: 'Represents prosperity, wealth, and good fortune. The hexagon that creates harmony in all six directions.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Wak (Six) - balance, harmony, the hexagon. The sixth day. The six directions that create balance.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'The sixth day', 'Wak', 'Harmony'],
        associations: ['Wak', 'Six', 'Balance', 'Harmony', 'Sixth day', 'Six directions'],
        luckyStatus: 'lucky',
        notes: 'Represents the sixth day and the principle of balance. The hexagon that creates harmony in all six directions.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Sé (Six) - balance, harmony, the hexagon, prosperity. The six directions of the world.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'Prosperity', 'Sé', 'Harmony'],
        associations: ['Six', 'Balance', 'Harmony', 'Prosperity', 'Hexagon', 'Six directions'],
        luckyStatus: 'lucky',
        notes: 'Represents balance, harmony, and prosperity. The hexagon that creates harmony in all six directions of the world.'
      },
      {
        tradition: 'Native American',
        meaning: 'Six - balance, harmony, the hexagon, the six directions. Sacred balance. The heart of the world.',
        symbolism: ['Six', 'Hexagon', 'Six directions', 'Balance', 'Sacred', 'Heart'],
        associations: ['Six directions', 'Balance', 'Harmony', 'Sacred', 'Hexagon', 'Heart'],
        luckyStatus: 'sacred',
        notes: 'Represents the six directions and sacred balance. The hexagon that creates the heart of the world.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The six, balance, harmony, the hexagon. The six directions. The heart center of creation.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Heart'],
        associations: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Heart'],
        luckyStatus: 'lucky',
        notes: 'Represents the six, balance, and harmony. The hexagon that creates the heart center of Egyptian creation.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Hexad - the six, balance, harmony, the hexagon. The six directions. The perfect balance of opposites.',
        symbolism: ['Hexad', 'Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions'],
        associations: ['Hexad', 'Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions'],
        luckyStatus: 'lucky',
        notes: 'Represents the Hexad, the six in Pythagorean philosophy. The hexagon that creates perfect balance of opposites.'
      },
      {
        tradition: 'Norse',
        meaning: 'The six, balance, harmony, the hexagon. The six directions. The heart of the world tree.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'World tree'],
        associations: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'World tree'],
        luckyStatus: 'lucky',
        notes: 'Represents the six, balance, and harmony. The hexagon that creates the heart of the world tree in Norse cosmology.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The six, balance, harmony, the hexagon. The six directions. The six perfections (paramitas) of the heart.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Six paramitas'],
        associations: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Six paramitas'],
        luckyStatus: 'sacred',
        notes: 'Represents the six, balance, and harmony. The hexagon of the six perfections (paramitas) that create the heart of compassion.'
      },
      {
        tradition: 'African',
        meaning: 'The six, balance, harmony, the hexagon. The six directions. The heart of community and love.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Heart'],
        associations: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Heart'],
        luckyStatus: 'lucky',
        notes: 'Represents the six, balance, and harmony. The hexagon that creates the heart of community and love.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The six, balance, harmony, the hexagon. The six directions in creation stories. The heart of the land.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Stories'],
        associations: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Stories'],
        luckyStatus: 'sacred',
        notes: 'Represents the six, balance, and harmony. The hexagon in creation stories that creates the heart of the land.'
      },
      {
        tradition: 'Korean',
        meaning: 'The six, balance, harmony, the hexagon. The six directions. The heart of prosperity and love.',
        symbolism: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Prosperity'],
        associations: ['Six', 'Hexagon', 'Balance', 'Harmony', 'Six directions', 'Prosperity'],
        luckyStatus: 'lucky',
        notes: 'Represents the six, balance, and harmony. The hexagon that creates the heart of prosperity and love.'
      }
    ]
  },
  
  7: {
    number: 7,
    isMaster: false,
    title: 'Septet',
    essence: 'Spirituality, wisdom, analysis, introspection, mystery',
    keywords: ['Spiritual', 'Analytical', 'Wise', 'Introspective', 'Mysterious', 'Intuitive'],
    
    coreMeaning: 'The number of spirituality, wisdom, and the inner quest. Represents the seven chakras, seven days, and the seeker of truth.',
    symbolicMeaning: 'The septagram, the seven-pointed star. The seeker, the hermit, the philosopher. The bridge between material and spiritual.',
    spiritualMeaning: 'The quest for truth and wisdom. Inner exploration and spiritual awakening. The hermit\'s path to enlightenment.',
    
    strengths: ['Spiritual', 'Analytical', 'Wise', 'Intuitive', 'Perfectionist', 'Philosophical', 'Independent', 'Mysterious'],
    challenges: ['Aloof', 'Critical', 'Secretive', 'Isolated', 'Perfectionist', 'Skeptical', 'Distant'],
    talents: ['Research', 'Philosophy', 'Science', 'Spirituality', 'Analysis', 'Teaching', 'Writing'],
    shadowAspects: ['Isolation', 'Perfectionism', 'Criticism', 'Secretiveness', 'Skepticism'],
    
    lifePurpose: 'Represents spirituality, wisdom, and the inner quest. Embodies analysis, introspection, and the search for truth.',
    lifeLessons: [
      'Balancing solitude with connection',
      'Trusting intuition over analysis',
      'Sharing wisdom without judgment',
      'Finding the divine in the ordinary',
      'Learning that truth is lived, not just studied'
    ],
    
    compatibleNumbers: [1, 5, 9],
    challengingNumbers: [2, 3, 4],
    relationshipDynamics: {
      1: 'Leadership with spiritual depth',
      2: 'Mystical connection, but may feel distant',
      3: 'Creative and spiritual partnership',
      4: 'Structure supports spiritual practice',
      5: 'Spiritual exploration together',
      6: 'Mystical love connection',
      7: 'Doubled wisdom, but risk of isolation',
      8: 'Material success with spiritual understanding',
      9: 'Perfect teacher-student dynamic'
    },
    
    careerPaths: [
      'Philosopher', 'Scientist', 'Researcher', 'Teacher',
      'Mystic', 'Writer', 'Analyst', 'Counselor',
      'Astronomer', 'Mathematician', 'Theologian', 'Hermit'
    ],
    vocationalCalling: 'Represents the seeking, analysis, and teaching of spiritual truth and wisdom.',
    
    color: 'Violet',
    element: 'Water',
    planet: 'Neptune',
    astrologicalCorrespondence: 'Pisces, Virgo',
    chakra: 'Crown (Sahasrara)',
    frequency: 963, // Awakening intuition and connection to divine
    
    symbols: ['Septagram', 'Labyrinth', 'Crystal', 'Lamp', 'Book', 'Star'],
    shapeDefinitions: {
      'Septagram': {
        geometricForm: 'Seven-pointed star (heptagram, 7 vertices)',
        description: 'A seven-pointed star formed by connecting every other vertex of a heptagon. The geometric expression of seven\'s mystical nature.',
        significance: 'Represents the seven chakras, seven planets, and the mystical expression of seven. The septagram shape shows how seven creates spiritual awakening. The seven points emphasize the comprehensive spiritual nature of seven.'
      },
      'Labyrinth': {
        geometricForm: 'Spiral or maze pattern (single path, 7-fold symmetry)',
        description: 'A spiral or maze pattern with seven-fold symmetry - the geometric expression of the inner journey.',
        significance: 'Represents the inner journey, spiritual seeking, and the path to wisdom. The labyrinth shape shows how seven creates the path to inner awakening. The spiral form emphasizes the inward journey of seven.'
      },
      'Crystal': {
        geometricForm: 'Hexagonal or cubic crystal structure (7-fold patterns)',
        description: 'A crystalline form with geometric structure - the solid expression of seven\'s clarity and wisdom.',
        significance: 'Represents clarity, wisdom, and the crystalline expression of seven. The crystal shape shows how seven creates clarity through inner work. The geometric structure emphasizes the ordered wisdom of seven.'
      },
      'Lamp': {
        geometricForm: 'Cylindrical or spherical form with light source',
        description: 'A form that contains and radiates light - the geometric expression of illumination and wisdom.',
        significance: 'Represents illumination, wisdom, and the light of understanding. The lamp shape shows how seven creates inner light through seeking. The radiating form emphasizes the wisdom that seven brings.'
      },
      'Book': {
        geometricForm: 'Rectangular form (pages, knowledge container)',
        description: 'A rectangular form containing knowledge - the geometric expression of wisdom and learning.',
        significance: 'Represents knowledge, wisdom, and the accumulated understanding of seven. The book shape shows how seven creates wisdom through study and reflection. The rectangular form emphasizes the structured knowledge of seven.'
      },
      'Star': {
        geometricForm: 'Seven-pointed star or constellation pattern',
        description: 'A star form with seven points or a constellation pattern - the celestial expression of seven.',
        significance: 'Represents the stars, cosmic wisdom, and the spiritual expression of seven. The star shape shows how seven connects to cosmic understanding. The points emphasize the spiritual illumination of seven.'
      }
    },
    mythologicalFigures: ['Hermes Trismegistus', 'Thoth', 'Hermit Sages', 'Merlin'],
    tarotCorrespondence: 'The Chariot',
    gemstones: ['Amethyst', 'Lapis Lazuli', 'Sapphire', 'Clear Quartz'],
    flowers: ['Lavender', 'Violet', 'Iris', 'Lily of the Valley'],
    
    favorableMonths: [7, 1, 4, 10],
    favorableDays: [7, 16, 25],
    peakYears: [25, 34, 43, 52, 61, 70],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Signifies loss and failure to some, yet to Buddhists it beams with auspicious essences. Complex meaning. The seven stars of the Big Dipper.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Mystery', 'Big Dipper', 'Auspicious'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Auspicious', 'Big Dipper', 'Complex'],
        luckyStatus: 'neutral',
        notes: 'A number mired in debate; signifies loss to some, yet to Buddhists it beams with auspicious essences. The seven stars of the Big Dipper guide spiritual seekers.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Sapta (Seven) - the seven chakras, the seven sages, spiritual awakening, wisdom. The seven worlds and seven rivers.',
        symbolism: ['Seven', 'Septagram', 'Seven chakras', 'Spiritual', 'Seven sages', 'Wisdom'],
        associations: ['Seven chakras', 'Seven sages', 'Spiritual', 'Wisdom', 'Seven worlds', 'Seven rivers'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven chakras and spiritual awakening. The seven sages (Saptarishi) and seven worlds of existence.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Sab\'a (Seven) - the seven heavens, spiritual perfection, completion, wisdom. The seven earths and seven seas.',
        symbolism: ['Seven', 'Septagram', 'Seven heavens', 'Spiritual', 'Perfection', 'Wisdom'],
        associations: ['Seven heavens', 'Spiritual perfection', 'Wisdom', 'Completion', 'Seven earths', 'Seven seas'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven heavens and spiritual perfection. The seven earths and seven seas create the complete cosmology.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Zayin (ז) - the seventh letter, sword, weapon. The seven days of creation, spiritual completion. The seven sefirot of the lower face.',
        symbolism: ['Zayin', 'Sword', 'Seven', 'Spiritual', 'Seven days', 'Completion'],
        associations: ['Zayin', 'Netzach (Victory)', 'Seven days', 'Spiritual', 'Seven sefirot', 'Completion'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven days of creation and spiritual completion. Zayin as the sword of spiritual victory and the seven sefirot.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Nana/Shichi (七) - seven, spiritual, mysterious, auspicious. The seven gods of fortune and seven virtues.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Mystery', 'Auspicious', 'Seven gods'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Auspicious', 'Seven gods of fortune', 'Seven virtues'],
        luckyStatus: 'lucky',
        notes: 'Represents spiritual mystery and auspiciousness. The seven gods of fortune (Shichifukujin) and seven virtues.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Huk (Seven) - spiritual, mysterious, the septagram. The seventh day. The seven directions including center, above, and below.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'The seventh day', 'Huk', 'Seven directions'],
        associations: ['Huk', 'Seven', 'Spiritual', 'Mystery', 'Seventh day', 'Seven directions'],
        luckyStatus: 'lucky',
        notes: 'Represents the seventh day and spiritual mystery. The seven directions (four cardinal, center, above, below) create complete orientation.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Seacht (Seven) - spiritual, mysterious, the seven directions, wisdom. The seven sacred trees and seven years of cycles.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Wisdom', 'Seven directions', 'Sacred'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Wisdom', 'Seven directions', 'Seven trees'],
        luckyStatus: 'sacred',
        notes: 'Represents spiritual mystery and wisdom. The seven directions and seven sacred trees create the foundation of Celtic cosmology.'
      },
      {
        tradition: 'Native American',
        meaning: 'Seven - spiritual, mysterious, the seven directions, wisdom. Sacred number. The seven clans and seven generations.',
        symbolism: ['Seven', 'Septagram', 'Seven directions', 'Spiritual', 'Sacred', 'Wisdom'],
        associations: ['Seven directions', 'Spiritual', 'Wisdom', 'Sacred', 'Seven clans', 'Seven generations'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven directions and is considered highly sacred. The seven clans and seven generations create the foundation of community.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The seven, spiritual, mysterious. The seven Hathors, the seven souls, and the seven gates of the underworld.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Mystery', 'Seven Hathors', 'Seven gates'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Seven Hathors', 'Seven souls', 'Seven gates'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven, spiritual mystery. The seven Hathors, seven souls (Ba), and seven gates of the underworld.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Heptad - the seven, spiritual perfection, wisdom. The seven planets, seven virtues, and seven liberal arts.',
        symbolism: ['Heptad', 'Seven', 'Septagram', 'Spiritual', 'Perfection', 'Wisdom'],
        associations: ['Heptad', 'Seven', 'Spiritual', 'Perfection', 'Seven planets', 'Seven virtues'],
        luckyStatus: 'sacred',
        notes: 'Represents the Heptad, the seven in Pythagorean philosophy. The seven planets, seven virtues, and seven liberal arts create complete knowledge.'
      },
      {
        tradition: 'Norse',
        meaning: 'The seven, spiritual, mysterious. The seven worlds, seven days, and the seven runes of wisdom.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Mystery', 'Seven worlds', 'Wisdom'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Seven worlds', 'Seven days', 'Seven runes'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven, spiritual mystery. The seven worlds of Yggdrasil and seven runes of wisdom.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The seven, spiritual perfection, wisdom. The seven chakras, seven Buddhas, and seven aspects of enlightenment.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Perfection', 'Seven chakras', 'Wisdom'],
        associations: ['Seven', 'Spiritual', 'Perfection', 'Seven chakras', 'Seven Buddhas', 'Enlightenment'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven, spiritual perfection. The seven chakras, seven Buddhas, and seven aspects of enlightenment.'
      },
      {
        tradition: 'African',
        meaning: 'The seven, spiritual, mysterious. The seven directions, seven ancestors, and seven principles of life.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Mystery', 'Seven directions', 'Wisdom'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Seven directions', 'Seven ancestors', 'Seven principles'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven, spiritual mystery. The seven directions, seven ancestors, and seven principles of life.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The seven, spiritual, mysterious. The seven directions in creation stories. The seven songlines and seven ancestors.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Mystery', 'Seven directions', 'Stories'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Seven directions', 'Seven songlines', 'Seven ancestors'],
        luckyStatus: 'sacred',
        notes: 'Represents the seven, spiritual mystery. The seven directions in creation stories, seven songlines, and seven ancestors.'
      },
      {
        tradition: 'Korean',
        meaning: 'The seven, spiritual, mysterious, auspicious. The seven stars and seven virtues. The septagram of wisdom.',
        symbolism: ['Seven', 'Septagram', 'Spiritual', 'Mystery', 'Auspicious', 'Wisdom'],
        associations: ['Seven', 'Spiritual', 'Mystery', 'Auspicious', 'Seven stars', 'Seven virtues'],
        luckyStatus: 'lucky',
        notes: 'Represents the seven, spiritual mystery, and auspiciousness. The seven stars and seven virtues create wisdom.'
      }
    ]
  },
  
  8: {
    number: 8,
    isMaster: false,
    title: 'Octagon',
    essence: 'Power, abundance, authority, material mastery, balance',
    keywords: ['Power', 'Abundance', 'Authority', 'Success', 'Material', 'Mastery'],
    
    coreMeaning: 'The number of power, material success, and karmic balance. Represents infinity, abundance, and the law of cause and effect.',
    symbolicMeaning: 'The infinity symbol, the eight-spoked wheel. Balance between material and spiritual. The power of manifestation.',
    spiritualMeaning: 'Material mastery as spiritual practice. Using power wisely. Understanding that true abundance comes from balance.',
    
    strengths: ['Powerful', 'Ambitious', 'Authoritative', 'Successful', 'Efficient', 'Organized', 'Confident', 'Material Mastery'],
    challenges: ['Materialistic', 'Workaholic', 'Domineering', 'Ruthless', 'Power-Hungry', 'Controlling', 'Imbalanced'],
    talents: ['Business', 'Finance', 'Management', 'Leadership', 'Organization', 'Material Creation'],
    shadowAspects: ['Greed', 'Power Abuse', 'Materialism', 'Workaholism', 'Ruthlessness'],
    
    lifePurpose: 'Represents power, material mastery, and abundance. Embodies authority, success, and the balanced use of resources.',
    lifeLessons: [
      'Balancing material and spiritual',
      'Using power to serve, not dominate',
      'Understanding that true abundance is spiritual',
      'Learning that giving creates receiving',
      'Mastering the material to transcend it'
    ],
    
    compatibleNumbers: [2, 4, 6],
    challengingNumbers: [1, 3, 5],
    relationshipDynamics: {
      1: 'Power struggles, but potential for great achievement',
      2: 'Powerful partnership in business, needs emotional balance',
      3: 'Creative business success possible',
      4: 'Powerful material manifestation together',
      5: 'Dynamic business partnership',
      6: 'Material success with loving support',
      7: 'Material success with spiritual understanding',
      8: 'Doubled power, but risk of conflict',
      9: 'Material resources for humanitarian service'
    },
    
    careerPaths: [
      'CEO', 'Banker', 'Investor', 'Executive',
      'Manager', 'Entrepreneur', 'Real Estate', 'Lawyer',
      'Judge', 'Politician', 'Finance', 'Business Owner'
    ],
    vocationalCalling: 'Represents mastery of material resources and the creation of abundance through power and authority.',
    
    color: 'Gold',
    element: 'Earth',
    planet: 'Saturn',
    astrologicalCorrespondence: 'Capricorn, Scorpio',
    chakra: 'Root (Muladhara)',
    frequency: 432, // Natural tuning, cosmic harmony
    
    symbols: ['Infinity', 'Eight-Spoked Wheel', 'Octagon', 'Scales', 'Crown', 'Scepter'],
    shapeDefinitions: {
      'Infinity': {
        geometricForm: 'Lemniscate (∞, horizontal figure-eight)',
        description: 'A horizontal figure-eight (∞) - the geometric expression of infinity and eternal cycles. The symbol of endless flow and balance.',
        significance: 'Represents infinity, eternal cycles, and the karmic expression of eight. The infinity shape shows how eight creates balance through cycles. The continuous loop emphasizes the eternal nature of cause and effect.'
      },
      'Eight-Spoked Wheel': {
        geometricForm: 'Circle with 8 spokes (Dharma wheel, 8 directions)',
        description: 'A circular form with eight spokes radiating outward - the geometric expression of the eight-fold path and complete cycles.',
        significance: 'Represents the eight-fold path, complete cycles, and the comprehensive expression of eight. The eight-spoked wheel shows how eight creates complete understanding. The eight spokes emphasize the comprehensive nature of eight\'s power.'
      },
      'Octagon': {
        geometricForm: 'Octagon (8 vertices, 8 equal sides, 135° angles)',
        description: 'An eight-sided polygon with equal sides - the perfect expression of balance and power. The geometric form that creates stability through eight.',
        significance: 'Represents balance, power, and the stable expression of eight. The octagon is the geometric expression of eight - the form that creates balance through eight directions. The eight equal sides show how eight creates perfect balance and power.',
        practicalApplication: 'The Octagon is your power balancer. It shows you how to achieve success while maintaining balance in all 8 areas of life. Like a stop sign that commands respect, the Octagon teaches you to use your power wisely and create balance in all directions. When power feels overwhelming or imbalanced, the Octagon reminds you to check all 8 directions: career, finances, health, relationships, creativity, spirituality, service, and personal growth.',
        dailyLifeExamples: [
          'Balancing 8 areas of life: career success with personal fulfillment, material abundance with spiritual growth',
          'Using your authority and power to create positive change while staying humble and balanced',
          'Building wealth through 8 streams: salary, investments, side business, passive income, savings, assets, skills, and network',
          'Creating a leadership style that balances 8 qualities: strength with compassion, ambition with patience, authority with humility',
          'Managing time across 8 life domains: work, family, health, finances, learning, creativity, service, and rest'
        ],
        howToWorkWithIt: [
          'When power feels imbalanced, check all 8 directions of your life and restore equilibrium',
          'Use the Octagon principle in decision-making: consider 8 perspectives before taking action',
          'Create octagonal structures in your environment: use 8-sided objects, arrange items in octagonal patterns',
          'Practice the Octagon meditation: visualize your life as an octagon with 8 equal sides, rotating to see which side needs attention',
          'When building success, ensure you\'re growing in all 8 areas simultaneously, not just one or two',
          'Use the Octagon to balance giving and receiving: ensure you\'re not only taking or only giving, but creating flow in all directions'
        ]
      },
      'Scales': {
        geometricForm: 'Two equal pans on horizontal beam (balance)',
        description: 'A horizontal beam with two equal pans - the geometric expression of justice and balance.',
        significance: 'Represents justice, balance, and the karmic expression of eight. The scales shape shows how eight creates balance through justice. The equal pans emphasize the perfect balance of cause and effect.'
      },
      'Crown': {
        geometricForm: 'Circular or octagonal base with points (8 points)',
        description: 'A form with a circular or octagonal base and points - the geometric expression of authority and power.',
        significance: 'Represents authority, power, and the elevated expression of eight. The crown shape shows how eight creates authority through balance. The points emphasize the power and success of eight.'
      },
      'Scepter': {
        geometricForm: 'Vertical staff with ornamental top (power symbol)',
        description: 'A vertical staff with an ornamental top - the linear expression of authority and power.',
        significance: 'Represents authority, power, and the commanding expression of eight. The scepter shape shows how eight creates authority through balanced power. The vertical form emphasizes the elevated status of eight.'
      }
    },
    mythologicalFigures: ['Zeus', 'Odin', 'Jupiter', 'Kings and Emperors'],
    tarotCorrespondence: 'Strength',
    gemstones: ['Gold', 'Tiger\'s Eye', 'Pyrite', 'Citrine'],
    flowers: ['Sunflower', 'Marigold', 'Goldenrod', 'Dahlia'],
    
    favorableMonths: [8, 2, 5, 11],
    favorableDays: [8, 17, 26],
    peakYears: [26, 35, 44, 53, 62, 71],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'Audibly echoing "prosper" (bā), eight symbolizes growth and flourishing richness. Very lucky. The infinity symbol of endless prosperity.',
        symbolism: ['Eight', 'Infinity', 'Prosperity', 'Abundance', 'Growth', 'Richness'],
        associations: ['Prosperity', 'Abundance', 'Growth', 'Infinity', 'Very lucky', 'Flourishing'],
        luckyStatus: 'lucky',
        notes: 'Audibly echoing "prosper", eight symbolizes growth and flourishing richness. Very lucky number. The infinity symbol creates endless prosperity.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Ashta (Eight) - the eight directions, infinity, abundance, material and spiritual balance. The eight limbs of yoga and eight siddhis.',
        symbolism: ['Eight', 'Infinity', 'Eight directions', 'Balance', 'Abundance', 'Eight limbs'],
        associations: ['Eight directions', 'Infinity', 'Abundance', 'Balance', 'Eight limbs of yoga', 'Eight siddhis'],
        luckyStatus: 'lucky',
        notes: 'Represents the eight directions and the balance of material and spiritual. The eight limbs of yoga and eight siddhis (powers).'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Thamaniya (Eight) - abundance, infinity, the eight gates of paradise, prosperity. The eight angels carrying the throne.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Prosperity', 'Eight gates', 'Paradise'],
        associations: ['Eight gates', 'Infinity', 'Abundance', 'Prosperity', 'Eight angels', 'Paradise'],
        luckyStatus: 'lucky',
        notes: 'Represents the eight gates of paradise and abundance. The eight angels who carry the divine throne.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Chet (ח) - the eighth letter, fence, life. The eight days of dedication, abundance. Hod (Splendor) - the eighth sefirah.',
        symbolism: ['Chet', 'Fence', 'Life', 'Eight', 'Eight days', 'Splendor'],
        associations: ['Chet', 'Hod (Splendor)', 'Eight days', 'Abundance', 'Fence', 'Life'],
        luckyStatus: 'lucky',
        notes: 'Represents the eight days of dedication and abundance. Chet as the fence of life and Hod as the splendor of manifestation.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Hachi (八) - eight, prosperity, abundance, infinity. Very lucky. The eight-fold path and eight virtues.',
        symbolism: ['Eight', 'Infinity', 'Prosperity', 'Abundance', 'Very lucky', 'Eight-fold'],
        associations: ['Eight', 'Prosperity', 'Abundance', 'Infinity', 'Eight-fold path', 'Eight virtues'],
        luckyStatus: 'lucky',
        notes: 'Represents prosperity, abundance, and infinity. Very lucky number. The eight-fold path and eight virtues create complete wisdom.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Waxak (Eight) - abundance, infinity, the eight directions. The eighth day. The infinity of cycles and abundance.',
        symbolism: ['Eight', 'Infinity', 'Eight directions', 'The eighth day', 'Waxak', 'Abundance'],
        associations: ['Waxak', 'Eight', 'Abundance', 'Infinity', 'Eighth day', 'Eight directions'],
        luckyStatus: 'lucky',
        notes: 'Represents the eighth day and the principle of abundance. The infinity symbol creates endless cycles of prosperity.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Ocht (Eight) - abundance, infinity, prosperity, the eight directions. The eight-fold wheel of the year.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Prosperity', 'Ocht', 'Eight directions'],
        associations: ['Eight', 'Abundance', 'Infinity', 'Prosperity', 'Eight directions', 'Wheel of year'],
        luckyStatus: 'lucky',
        notes: 'Represents abundance, infinity, and prosperity. The eight directions and eight-fold wheel of the year create complete cycles.'
      },
      {
        tradition: 'Native American',
        meaning: 'Eight - abundance, infinity, the eight directions, prosperity. Sacred abundance. The infinity of the great circle.',
        symbolism: ['Eight', 'Infinity', 'Eight directions', 'Abundance', 'Sacred', 'Great circle'],
        associations: ['Eight directions', 'Abundance', 'Infinity', 'Sacred', 'Great circle', 'Prosperity'],
        luckyStatus: 'sacred',
        notes: 'Represents the eight directions and sacred abundance. The infinity of the great circle creates endless prosperity.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The eight, abundance, infinity. The eight gods of Hermopolis (Ogdoad). The infinity of creation and abundance.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Eight gods', 'Ogdoad', 'Creation'],
        associations: ['Eight', 'Infinity', 'Abundance', 'Eight gods', 'Ogdoad', 'Creation'],
        luckyStatus: 'sacred',
        notes: 'Represents the eight, abundance, and infinity. The eight gods of Hermopolis (Ogdoad) create the foundation of creation.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Ogdoad - the eight, infinity, abundance. The eight spheres of the cosmos. The infinity of material and spiritual prosperity.',
        symbolism: ['Ogdoad', 'Eight', 'Infinity', 'Abundance', 'Eight spheres', 'Prosperity'],
        associations: ['Ogdoad', 'Eight', 'Infinity', 'Abundance', 'Eight spheres', 'Prosperity'],
        luckyStatus: 'lucky',
        notes: 'Represents the Ogdoad, the eight in Pythagorean philosophy. The eight spheres of the cosmos create infinite abundance.'
      },
      {
        tradition: 'Norse',
        meaning: 'The eight, abundance, infinity. The eight-legged horse Sleipnir. The infinity of the world tree and abundance.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Sleipnir', 'World tree', 'Prosperity'],
        associations: ['Eight', 'Infinity', 'Abundance', 'Sleipnir', 'World tree', 'Prosperity'],
        luckyStatus: 'lucky',
        notes: 'Represents the eight, abundance, and infinity. The eight-legged horse Sleipnir and the infinity of the world tree.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The eight, abundance, infinity. The eight auspicious symbols. The infinity of spiritual and material prosperity.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Eight symbols', 'Prosperity', 'Auspicious'],
        associations: ['Eight', 'Infinity', 'Abundance', 'Eight symbols', 'Prosperity', 'Auspicious'],
        luckyStatus: 'sacred',
        notes: 'Represents the eight, abundance, and infinity. The eight auspicious symbols create complete spiritual and material prosperity.'
      },
      {
        tradition: 'African',
        meaning: 'The eight, abundance, infinity. The eight directions and eight principles. The infinity of community prosperity.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Eight directions', 'Eight principles', 'Prosperity'],
        associations: ['Eight', 'Infinity', 'Abundance', 'Eight directions', 'Eight principles', 'Prosperity'],
        luckyStatus: 'lucky',
        notes: 'Represents the eight, abundance, and infinity. The eight directions and eight principles create community prosperity.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The eight, abundance, infinity. The eight directions in creation stories. The infinity of the land and abundance.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Eight directions', 'Stories', 'Land'],
        associations: ['Eight', 'Infinity', 'Abundance', 'Eight directions', 'Stories', 'Land'],
        luckyStatus: 'sacred',
        notes: 'Represents the eight, abundance, and infinity. The eight directions in creation stories create the infinity of the land.'
      },
      {
        tradition: 'Korean',
        meaning: 'The eight, abundance, infinity, prosperity. The eight directions and eight virtues. The infinity of harmony and wealth.',
        symbolism: ['Eight', 'Infinity', 'Abundance', 'Prosperity', 'Eight directions', 'Harmony'],
        associations: ['Eight', 'Infinity', 'Abundance', 'Prosperity', 'Eight directions', 'Harmony'],
        luckyStatus: 'lucky',
        notes: 'Represents the eight, abundance, infinity, and prosperity. The eight directions and eight virtues create harmony and wealth.'
      }
    ]
  },
  
  9: {
    number: 9,
    isMaster: false,
    title: 'Ennead',
    essence: 'Completion, compassion, universal love, wisdom, service',
    keywords: ['Compassion', 'Humanitarian', 'Completion', 'Wisdom', 'Universal Love', 'Service'],
    
    coreMeaning: 'The number of completion, universal love, and humanitarian service. Represents the end of a cycle and preparation for new beginnings.',
    symbolicMeaning: 'The circle, the enneagram, the highest single digit. Completion, wholeness, and the return to unity through service.',
    spiritualMeaning: 'Universal compassion and service. The Bodhisattva path. Completing cycles to begin anew. Love without conditions.',
    
    strengths: ['Compassionate', 'Humanitarian', 'Generous', 'Wise', 'Idealistic', 'Tolerant', 'Selfless', 'Universal'],
    challenges: ['Self-Sacrificing', 'Impractical', 'Moody', 'Unfocused', 'Martyrdom', 'Perfectionist', 'Overwhelmed'],
    talents: ['Healing', 'Teaching', 'Service', 'Art', 'Writing', 'Counseling', 'Humanitarian Work'],
    shadowAspects: ['Martyrdom', 'Perfectionism', 'Self-Sacrifice', 'Overwhelmed'],
    
    lifePurpose: 'Represents completion, humanitarianism, and universal love. Embodies compassion, service, and the end of cycles.',
    lifeLessons: [
      'Learning to serve without losing self',
      'Balancing idealism with practicality',
      'Completing what is started',
      'Understanding that all are one',
      'Releasing attachment to outcomes'
    ],
    
    compatibleNumbers: [3, 6, 9],
    challengingNumbers: [1, 5, 7],
    relationshipDynamics: {
      1: 'Humanitarian service may challenge individual focus',
      2: 'Compassionate service together',
      3: 'Creative humanitarian expression',
      4: 'Structure for humanitarian service',
      5: 'Adventurous humanitarian service',
      6: 'Perfect humanitarian partnership',
      7: 'Perfect teacher-student dynamic',
      8: 'Material resources for humanitarian service',
      9: 'Maximum compassion and service'
    },
    
    careerPaths: [
      'Healer', 'Teacher', 'Counselor', 'Artist',
      'Writer', 'Humanitarian', 'Nurse', 'Social Worker',
      'Volunteer', 'Activist', 'Minister', 'Therapist'
    ],
    vocationalCalling: 'Represents service to humanity, completion of cycles, and expression of universal love.',
    
    color: 'All Colors (Rainbow)',
    element: 'Fire',
    planet: 'Mars',
    astrologicalCorrespondence: 'Aries, Sagittarius',
    chakra: 'Crown (Sahasrara)',
    frequency: 528, // Love and transformation
    
    symbols: ['Circle', 'Enneagram', 'Nine-Pointed Star', 'Phoenix', 'Infinity'],
    shapeDefinitions: {
      'Circle': {
        geometricForm: 'Circle (infinite vertices, perfect unity)',
        description: 'A perfect circle with no beginning or end - the geometric expression of completion and unity. The form that contains all possibilities.',
        significance: 'Represents completion, unity, and the perfect expression of nine. The circle is the geometric expression of nine - the form that completes all cycles. The infinite nature emphasizes the universal love and completion of nine.'
      },
      'Enneagram': {
        geometricForm: 'Circle with 9 points connected by lines (inner triangle and hexad)',
        description: 'A circle with nine points connected by lines, forming an inner triangle and hexad - the geometric expression of the nine personality types and completion.',
        significance: 'Represents the nine personality types, completion, and the comprehensive expression of nine. The enneagram shape shows how nine creates complete understanding. The nine points emphasize the comprehensive nature of nine\'s wisdom.'
      },
      'Nine-Pointed Star': {
        geometricForm: 'Nine-pointed star (nonagram, 9 vertices)',
        description: 'A nine-pointed star formed by connecting points on a circle - the geometric expression of completion and universal love.',
        significance: 'Represents universal love, completion, and the radiant expression of nine. The nine-pointed star shows how nine creates universal understanding. The nine points emphasize the comprehensive, loving nature of nine.'
      },
      'Phoenix': {
        geometricForm: 'Rising form with wings (circular/cyclical pattern)',
        description: 'A form that rises and transforms - the geometric expression of completion through transformation and rebirth.',
        significance: 'Represents transformation, completion, and the cyclical expression of nine. The phoenix shape shows how nine creates completion through transformation. The rising form emphasizes the rebirth and completion inherent in nine.'
      },
      'Infinity': {
        geometricForm: 'Lemniscate (∞, horizontal figure-eight) or complete circle',
        description: 'A horizontal figure-eight or complete circle - the geometric expression of infinite completion and universal love.',
        significance: 'Represents infinite completion, universal love, and the endless expression of nine. The infinity shape shows how nine creates completion that extends infinitely. The continuous form emphasizes the universal, loving nature of nine.'
      }
    },
    mythologicalFigures: ['Bodhisattvas', 'Quan Yin', 'Saints', 'Avatars'],
    tarotCorrespondence: 'The Hermit',
    gemstones: ['Clear Quartz', 'Diamond', 'Rainbow Quartz', 'Opal'],
    flowers: ['Lotus', 'Lily', 'Rose', 'All Flowers'],
    
    favorableMonths: [9, 3, 6, 12],
    favorableDays: [9, 18, 27],
    peakYears: [27, 36, 45, 54, 63, 72],

    culturalArchetypes: [
      {
        tradition: 'Chinese',
        meaning: 'The numeral of the "emperor", nine represents eternity, perfection, and longevity. Very auspicious. The nine dragons and nine heavens.',
        symbolism: ['Nine', 'Circle', 'Eternity', 'Perfection', 'Emperor', 'Longevity'],
        associations: ['Emperor', 'Eternity', 'Perfection', 'Longevity', 'Nine dragons', 'Nine heavens'],
        luckyStatus: 'lucky',
        notes: 'The numeral of the emperor, nine represents eternity, perfection, and longevity. Very auspicious. The nine dragons and nine heavens create complete cosmic order.'
      },
      {
        tradition: 'Indian/Vedic',
        meaning: 'Nava (Nine) - completion, the nine planets, the enneagram, universal love, wisdom. The nine forms of the divine and nine rasas.',
        symbolism: ['Nine', 'Circle', 'Nine planets', 'Completion', 'Enneagram', 'Universal love'],
        associations: ['Nine planets', 'Enneagram', 'Completion', 'Universal love', 'Nine forms', 'Nine rasas'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine planets and the completion of cycles. The nine forms of the divine and nine rasas (emotional essences) create complete expression.'
      },
      {
        tradition: 'Arabic/Islamic',
        meaning: 'Tis\'a (Nine) - completion, the nine heavens, perfection, universal love. The nine names of Allah and nine attributes.',
        symbolism: ['Nine', 'Circle', 'Nine heavens', 'Completion', 'Perfection', 'Universal love'],
        associations: ['Nine heavens', 'Completion', 'Perfection', 'Universal love', 'Nine names', 'Nine attributes'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine heavens and completion. The nine names of Allah and nine attributes create perfect divine expression.'
      },
      {
        tradition: 'Hebrew/Gematria',
        meaning: 'Tet (ט) - the ninth letter, snake, completion. Yesod (Foundation), the nine sefirot below. The completion of the lower tree.',
        symbolism: ['Tet', 'Snake', 'Completion', 'Nine', 'Yesod', 'Nine sefirot'],
        associations: ['Tet', 'Yesod (Foundation)', 'Nine sefirot', 'Completion', 'Snake', 'Lower tree'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine sefirot below and completion. Tet as the snake of transformation and Yesod as the foundation of the lower tree.'
      },
      {
        tradition: 'Japanese',
        meaning: 'Kyū (九) - nine, completion, perfection, eternity. Very auspicious. The nine-fold path and nine virtues.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Eternity', 'Auspicious'],
        associations: ['Nine', 'Completion', 'Perfection', 'Eternity', 'Nine-fold path', 'Nine virtues'],
        luckyStatus: 'lucky',
        notes: 'Represents completion, perfection, and eternity. Very auspicious. The nine-fold path and nine virtues create complete wisdom.'
      },
      {
        tradition: 'Mayan',
        meaning: 'Bolon (Nine) - completion, perfection, the nine levels of the underworld. The ninth day. The complete cycle of transformation.',
        symbolism: ['Nine', 'Circle', 'Completion', 'The ninth day', 'Bolon', 'Nine levels'],
        associations: ['Bolon', 'Nine', 'Completion', 'Perfection', 'Ninth day', 'Nine levels'],
        luckyStatus: 'sacred',
        notes: 'Represents the ninth day and the completion of cycles. The nine levels of the underworld create complete transformation.'
      },
      {
        tradition: 'Celtic',
        meaning: 'Naoi (Nine) - completion, perfection, the nine realms, universal love. The nine-fold wheel and nine sacred trees.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine realms', 'Universal love'],
        associations: ['Nine', 'Completion', 'Perfection', 'Universal love', 'Nine realms', 'Nine trees'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine realms and completion. The nine-fold wheel and nine sacred trees create complete cosmic order.'
      },
      {
        tradition: 'Native American',
        meaning: 'Nine - completion, perfection, the nine directions, universal love. Sacred completion. The nine clans and nine generations.',
        symbolism: ['Nine', 'Circle', 'Nine directions', 'Completion', 'Sacred', 'Perfection'],
        associations: ['Nine directions', 'Completion', 'Perfection', 'Sacred', 'Nine clans', 'Nine generations'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine directions and is considered sacred completion. The nine clans and nine generations create complete community wisdom.'
      },
      {
        tradition: 'Egyptian',
        meaning: 'The nine, completion, perfection. The nine gods of Heliopolis (Ennead). The complete circle of creation and completion.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Ennead', 'Nine gods'],
        associations: ['Nine', 'Circle', 'Completion', 'Perfection', 'Ennead', 'Nine gods'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine, completion, and perfection. The nine gods of Heliopolis (Ennead) create the complete circle of creation.'
      },
      {
        tradition: 'Greek/Roman',
        meaning: 'The Ennead - the nine, completion, perfection. The nine Muses and nine spheres. The complete circle of knowledge and art.',
        symbolism: ['Ennead', 'Nine', 'Circle', 'Completion', 'Perfection', 'Nine Muses'],
        associations: ['Ennead', 'Nine', 'Circle', 'Completion', 'Perfection', 'Nine Muses'],
        luckyStatus: 'sacred',
        notes: 'Represents the Ennead, the nine in Pythagorean philosophy. The nine Muses and nine spheres create complete knowledge and art.'
      },
      {
        tradition: 'Norse',
        meaning: 'The nine, completion, perfection. The nine worlds of Yggdrasil and nine nights of Odin\'s sacrifice. The complete circle of existence.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine worlds', 'Yggdrasil'],
        associations: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine worlds', 'Yggdrasil'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine, completion, and perfection. The nine worlds of Yggdrasil and nine nights create the complete circle of existence.'
      },
      {
        tradition: 'Tibetan',
        meaning: 'The nine, completion, perfection. The nine yanas (vehicles) and nine stages of the path. The complete circle of enlightenment.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine yanas', 'Enlightenment'],
        associations: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine yanas', 'Enlightenment'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine, completion, and perfection. The nine yanas (vehicles) and nine stages create the complete path to enlightenment.'
      },
      {
        tradition: 'African',
        meaning: 'The nine, completion, perfection. The nine directions and nine principles. The complete circle of community wisdom.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine directions', 'Wisdom'],
        associations: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine directions', 'Wisdom'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine, completion, and perfection. The nine directions and nine principles create complete community wisdom.'
      },
      {
        tradition: 'Aboriginal',
        meaning: 'The nine, completion, perfection. The nine directions in creation stories. The complete circle of the land and stories.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine directions', 'Stories'],
        associations: ['Nine', 'Circle', 'Completion', 'Perfection', 'Nine directions', 'Stories'],
        luckyStatus: 'sacred',
        notes: 'Represents the nine, completion, and perfection. The nine directions in creation stories create the complete circle of the land.'
      },
      {
        tradition: 'Korean',
        meaning: 'The nine, completion, perfection, eternity. The nine directions and nine virtues. The complete circle of harmony and wisdom.',
        symbolism: ['Nine', 'Circle', 'Completion', 'Perfection', 'Eternity', 'Harmony'],
        associations: ['Nine', 'Circle', 'Completion', 'Perfection', 'Eternity', 'Harmony'],
        luckyStatus: 'lucky',
        notes: 'Represents the nine, completion, perfection, and eternity. The nine directions and nine virtues create complete harmony and wisdom.'
      }
    ]
  },
  
  11: {
    number: 11,
    isMaster: true,
    title: 'Double Unity',
    essence: 'Master Number - Spiritual insight, intuition, enlightenment, inspiration',
    keywords: ['Intuitive', 'Inspiring', 'Visionary', 'Enlightened', 'Illuminated', 'Mystical'],
    
    coreMeaning: 'The first master number. Represents spiritual insight, intuition, and the ability to illuminate higher truth for others.',
    symbolicMeaning: 'Two pillars (1+1), the gateway between worlds. Higher octave of 2. The channel for divine inspiration.',
    spiritualMeaning: 'The awakened channel. Direct connection to higher consciousness. The ability to see beyond the veil.',
    masterNumberDescription: 'The master number of intuition and spiritual illumination. Not reduced because it represents a higher vibration.',
    manifestationLevel: 'Spiritual',
    
    strengths: ['Intuitive', 'Inspiring', 'Visionary', 'Idealistic', 'Enlightened', 'Sensitive', 'Insightful', 'Psychic'],
    challenges: ['Impractical', 'Nervous', 'Overly Sensitive', 'Stressed', 'Overwhelmed', 'Unfocused'],
    talents: ['Psychic Abilities', 'Channeling', 'Teaching', 'Inspiring', 'Visionary Work', 'Spiritual Guidance'],
    shadowAspects: ['Overwhelm', 'Nervousness', 'Unfocused Energy', 'Being Out of Body'],
    
    lifePurpose: 'Represents spiritual illumination, intuition, and inspiration. Embodies the bridge between worlds and higher consciousness.',
    lifeLessons: [
      'Grounding spiritual insights in practical reality',
      'Learning to handle high sensitivity',
      'Sharing visions without overwhelming',
      'Balancing inspiration with action',
      'Trusting intuition while maintaining discernment'
    ],
    
    compatibleNumbers: [2, 4, 6, 22],
    challengingNumbers: [1, 5, 8],
    relationshipDynamics: {
      2: 'Perfect intuitive and emotional connection',
      4: 'Intuition needs grounding structure',
      6: 'Inspirational love and service',
      22: 'Master builder with master illuminator - powerful combination',
      33: 'Perfect teacher-student or co-teacher dynamic'
    },
    
    careerPaths: [
      'Spiritual Teacher', 'Psychic', 'Intuitive Counselor', 'Medium',
      'Visionary', 'Inspiring Leader', 'Channel', 'Mystic',
      'Lightworker', 'Spiritual Guide', 'Inspirational Speaker'
    ],
    vocationalCalling: 'Represents illumination, inspiration, and the channeling of higher truth for the benefit of all.',
    
    color: 'Silver',
    element: 'Air',
    planet: 'Moon',
    astrologicalCorrespondence: 'Pisces, Cancer',
    chakra: 'Third Eye (Ajna)',
    frequency: 852, // Spiritual order and intuition
    
    symbols: ['Two Pillars', 'Gateway', 'Light', 'Eye', 'Tower', 'Lighthouse'],
    shapeDefinitions: {
      'Two Pillars': {
        geometricForm: 'Two parallel vertical lines (pillars, 2 vertical rectangles)',
        description: 'Two parallel vertical forms - the geometric expression of the gateway between worlds. Two pillars that support and frame.',
        significance: 'Represents the gateway between physical and spiritual realms, the dual nature of master number 11. The two pillars show how 11 creates a bridge between worlds. The parallel vertical forms emphasize the gateway and illumination of 11.'
      },
      'Gateway': {
        geometricForm: 'Arch or opening between two supports (archway)',
        description: 'An opening or arch between two supports - the geometric expression of passage and transition.',
        significance: 'Represents the gateway to higher understanding, the passage between realms. The gateway shape shows how 11 creates access to spiritual illumination. The arch form emphasizes the threshold and transition of 11.'
      },
      'Light': {
        geometricForm: 'Radiating form (rays, beams, or circular radiance)',
        description: 'A form that radiates light in all directions - the geometric expression of illumination and insight.',
        significance: 'Represents illumination, insight, and the light of understanding. The light shape shows how 11 brings spiritual light. The radiating form emphasizes the illuminating power of 11.'
      },
      'Eye': {
        geometricForm: 'Circle with triangle (eye of providence) or almond shape',
        description: 'A circular or almond-shaped form, often with triangular elements - the geometric expression of vision and insight.',
        significance: 'Represents spiritual vision, insight, and the ability to see beyond the physical. The eye shape shows how 11 creates spiritual sight. The form emphasizes the intuitive illumination of 11.'
      },
      'Tower': {
        geometricForm: 'Vertical line/rectangle extending upward (elevated structure)',
        description: 'A vertical form extending upward - the geometric expression of elevation and spiritual height.',
        significance: 'Represents spiritual elevation, insight, and the upward journey of 11. The tower shape shows how 11 creates spiritual height. The vertical form emphasizes the elevated nature of 11\'s illumination.'
      },
      'Lighthouse': {
        geometricForm: 'Cylindrical tower with light source (vertical cylinder with radiating top)',
        description: 'A vertical cylindrical form with a light source at the top - the geometric expression of guidance and illumination.',
        significance: 'Represents guidance, illumination, and the ability to light the way for others. The lighthouse shape shows how 11 creates spiritual guidance. The form emphasizes the illuminating and guiding power of 11.'
      }
    },
    mythologicalFigures: ['Prophets', 'Oracles', 'Seers', 'Mystics'],
    tarotCorrespondence: 'Justice',
    gemstones: ['Moonstone', 'Selenite', 'Clear Quartz', 'Labradorite'],
    flowers: ['White Lotus', 'Jasmine', 'Moonflower', 'Gardenia'],
    
    favorableMonths: [2, 11],
    favorableDays: [11, 29],
    peakYears: [11, 22, 29, 38, 47]
  },
  
  22: {
    number: 22,
    isMaster: true,
    title: 'Double Duality',
    essence: 'Master Number - Master manifestation, building, global vision, practical mastery',
    keywords: ['Visionary', 'Practical', 'Masterful', 'Confident', 'Powerful', 'Building'],
    
    coreMeaning: 'The second master number. The "Master Builder" who can turn grand visions into practical reality on a large scale.',
    symbolicMeaning: 'Two pillars of power (2+2), the foundation builder. Higher octave of 4. Building on a master level.',
    spiritualMeaning: 'Manifesting the divine plan in material form. Building structures that serve humanity. Practical spirituality at its highest.',
    masterNumberDescription: 'The master number of practical manifestation. The ability to build lasting structures that benefit all.',
    manifestationLevel: 'Physical',
    
    strengths: ['Visionary', 'Practical', 'Masterful', 'Confident', 'Powerful', 'Efficient', 'Ambitious', 'Influential'],
    challenges: ['Overwhelmed', 'Inner Tension', 'Overly Ambitious', 'Burnout', 'Perfectionism'],
    talents: ['Large-Scale Building', 'Organization', 'Manifestation', 'Leadership', 'Visionary Planning'],
    shadowAspects: ['Overwhelm', 'Burnout', 'Perfectionism', 'Control'],
    
    lifePurpose: 'Represents master manifestation and large-scale building. Embodies the transformation of grand visions into practical reality.',
    lifeLessons: [
      'Learning to build on grand scale without burnout',
      'Balancing vision with practical steps',
      'Serving humanity through building',
      'Understanding that true mastery serves all',
      'Building foundations that last generations'
    ],
    
    compatibleNumbers: [4, 8, 11, 33],
    challengingNumbers: [5, 7],
    relationshipDynamics: {
      4: 'Foundation building together',
      8: 'Material mastery at highest level',
      11: 'Master builder with master illuminator',
      22: 'Maximum building power, but needs balance',
      33: 'Building through teaching and service'
    },
    
    careerPaths: [
      'Master Architect', 'Large-Scale Builder', 'City Planner', 'Global Leader',
      'Infrastructure Developer', 'Systems Architect', 'Master Organizer', 'Visionary Entrepreneur'
    ],
    vocationalCalling: 'Represents the building of lasting structures and manifestation of grand visions for humanity.',
    
    color: 'Coral',
    element: 'Earth',
    planet: 'Saturn',
    astrologicalCorrespondence: 'Capricorn',
    chakra: 'Root (Muladhara)',
    frequency: 963, // Highest consciousness and manifestation
    
    symbols: ['Two Pillars', 'Foundation', 'Temple', 'Pyramid', 'City'],
    shapeDefinitions: {
      'Two Pillars': {
        geometricForm: 'Two parallel vertical lines (pillars, 2 vertical rectangles)',
        description: 'Two parallel vertical forms - the geometric expression of dual foundation and support. Two pillars that create stability.',
        significance: 'Represents the dual foundation of master number 22, showing how vision and practicality work together. The two pillars show how 22 creates stable structures. The parallel vertical forms emphasize the balanced foundation of 22.'
      },
      'Foundation': {
        geometricForm: 'Square base or rectangular platform (solid base)',
        description: 'A square or rectangular base that supports structures above - the geometric expression of solid foundation.',
        significance: 'Represents the solid foundation that 22 creates, the practical base for spiritual vision. The foundation shape shows how 22 builds lasting structures. The square/rectangular form emphasizes the stable, practical nature of 22.'
      },
      'Temple': {
        geometricForm: 'Rectangular structure with columns and roof (sacred architecture)',
        description: 'A rectangular structure with columns and roof - the geometric expression of sacred architecture and lasting structures.',
        significance: 'Represents the sacred structures that 22 creates, combining vision with practical building. The temple shape shows how 22 creates lasting, meaningful structures. The form emphasizes the master builder nature of 22.'
      },
      'Pyramid': {
        geometricForm: 'Square pyramid (4 triangular faces, square base)',
        description: 'A square pyramid - the geometric expression of structures that reach toward the divine while maintaining solid foundation.',
        significance: 'Represents the structures that 22 creates, reaching toward spiritual heights while maintaining practical foundation. The pyramid shape shows how 22 combines vision with practical building. The form emphasizes the master builder\'s ability to create lasting, elevated structures.'
      },
      'City': {
        geometricForm: 'Multiple structures in organized pattern (urban planning)',
        description: 'Multiple structures organized in a pattern - the geometric expression of large-scale building and organization.',
        significance: 'Represents the large-scale structures that 22 creates, the ability to build on a grand scale. The city shape shows how 22 creates comprehensive, organized structures. The pattern emphasizes the master builder\'s ability to create on a large scale.'
      }
    },
    mythologicalFigures: ['Master Builders', 'Solomon', 'Master Architects'],
    tarotCorrespondence: 'The Fool',
    gemstones: ['Tiger\'s Eye', 'Smoky Quartz', 'Obsidian', 'Black Tourmaline'],
    flowers: ['Oak', 'Cedar', 'Ivy', 'Pine'],
    
    favorableMonths: [4, 22],
    favorableDays: [22],
    peakYears: [22, 31, 40, 49]
  },
  
  33: {
    number: 33,
    isMaster: true,
    title: 'Triple Trinity',
    essence: 'Master Number - Compassion, teaching, healing, service, universal love',
    keywords: ['Compassionate', 'Nurturing', 'Healing', 'Inspiring', 'Selfless', 'Teaching'],
    
    coreMeaning: 'The third master number. The "Master Teacher" who combines compassion with practical service to heal and uplift humanity.',
    symbolicMeaning: 'Three pillars of love (3+3), the divine trinity in service. Higher octave of 6. Love in highest expression.',
    spiritualMeaning: 'Universal compassion in action. The Bodhisattva ideal. Teaching through love and service. Healing through presence.',
    masterNumberDescription: 'The master number of compassion and service. The highest expression of love through teaching and healing.',
    manifestationLevel: 'Emotional',
    
    strengths: ['Compassionate', 'Nurturing', 'Healing', 'Inspiring', 'Selfless', 'Wise', 'Loving', 'Service-Oriented'],
    challenges: ['Martyrdom', 'Overextended', 'Oversensitive', 'Boundary Issues', 'Burnout'],
    talents: ['Teaching', 'Healing', 'Counseling', 'Service', 'Nurturing', 'Inspiring'],
    shadowAspects: ['Martyrdom', 'Overextension', 'Boundary Loss', 'Self-Sacrifice'],
    
    lifePurpose: 'Represents master teaching, healing, and compassionate service. Embodies unconditional love and wisdom in action.',
    lifeLessons: [
      'Serving without losing self',
      'Teaching through example, not words alone',
      'Understanding that healing comes from presence',
      'Balancing compassion with boundaries',
      'Recognizing that true service empowers others'
    ],
    
    compatibleNumbers: [6, 9, 11, 22],
    challengingNumbers: [1, 8],
    relationshipDynamics: {
      6: 'Perfect compassionate partnership',
      9: 'Maximum humanitarian service',
      11: 'Perfect teacher-student or co-teacher dynamic',
      22: 'Building through teaching and service',
      33: 'Maximum compassion, but needs self-care'
    },
    
    careerPaths: [
      'Master Teacher', 'Healer', 'Counselor', 'Spiritual Leader',
      'Therapist', 'Nurturer', 'Compassionate Leader', 'Bodhisattva'
    ],
    vocationalCalling: 'Represents teaching, healing, and service through compassion and wisdom.',
    
    color: 'Emerald Green',
    element: 'Water',
    planet: 'Neptune',
    astrologicalCorrespondence: 'Pisces',
    chakra: 'Heart (Anahata)',
    frequency: 639, // Connection, compassion, healing
    
    symbols: ['Three Pillars', 'Heart', 'Lotus', 'Compassion', 'Service'],
    shapeDefinitions: {
      'Three Pillars': {
        geometricForm: 'Three parallel vertical lines (pillars, 3 vertical rectangles)',
        description: 'Three parallel vertical forms - the geometric expression of the trinity of service. Three pillars that support and elevate.',
        significance: 'Represents the three-fold nature of master number 33 - compassion, teaching, and service. The three pillars show how 33 creates comprehensive service. The parallel vertical forms emphasize the balanced, loving support of 33.'
      },
      'Heart': {
        geometricForm: 'Heart shape (two curves meeting at point, symmetrical)',
        description: 'A symmetrical form with two curves meeting at a point - the geometric expression of universal love and compassion.',
        significance: 'Represents universal love, compassion, and the heart-centered expression of 33. The heart shape shows how 33 creates love through service. The symmetrical form emphasizes the balanced, loving nature of 33.'
      },
      'Lotus': {
        geometricForm: 'Radial symmetry with multiple petals (often 8 or 9 petals)',
        description: 'A circular form with radial symmetry and multiple petals - the geometric expression of spiritual beauty and compassion.',
        significance: 'Represents spiritual beauty, compassion, and the elevated expression of 33. The lotus shape shows how 33 creates beauty through service. The radial symmetry emphasizes the balanced, loving, and spiritual nature of 33.'
      },
      'Compassion': {
        geometricForm: 'Open, embracing form (circular or heart-like)',
        description: 'An open, embracing form - the geometric expression of compassion and care.',
        significance: 'Represents compassion, care, and the loving expression of 33. The compassion shape shows how 33 creates love through understanding. The open form emphasizes the embracing, caring nature of 33.'
      },
      'Service': {
        geometricForm: 'Supporting structure or helping hand (uplifting form)',
        description: 'A form that supports and uplifts - the geometric expression of service and helping.',
        significance: 'Represents service, helping, and the practical expression of 33\'s compassion. The service shape shows how 33 creates help through love. The supporting form emphasizes the practical, loving service of 33.'
      }
    },
    mythologicalFigures: ['Bodhisattvas', 'Quan Yin', 'Jesus', 'Compassionate Teachers'],
    tarotCorrespondence: 'The Star',
    gemstones: ['Rose Quartz', 'Emerald', 'Rhodonite', 'Pink Tourmaline'],
    flowers: ['Lotus', 'Rose', 'Lily', 'Poppy'],
    
    favorableMonths: [3, 6, 9],
    favorableDays: [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33],
    peakYears: [33, 42, 51, 60]
  },
  
  44: {
    number: 44,
    isMaster: true,
    title: 'Double Square',
    essence: 'Master Number - Healing, balance, material & spiritual mastery, integration',
    keywords: ['Healing', 'Balanced', 'Integrated', 'Masterful', 'Grounded', 'Transformative'],
    
    coreMeaning: 'The fourth master number. The "Master Healer" who balances material and spiritual, creating wholeness and transformation.',
    symbolicMeaning: 'Four pillars of balance (4+4), perfect foundation. Higher octave of 8. Material and spiritual in harmony.',
    spiritualMeaning: 'Healing through integration. Balancing all aspects of being. Creating wholeness through mastery of opposites.',
    masterNumberDescription: 'The master number of integration and healing. Balancing material and spiritual to create wholeness.',
    manifestationLevel: 'Mental',
    
    strengths: ['Healing', 'Balanced', 'Integrated', 'Grounded', 'Masterful', 'Transformative', 'Practical', 'Spiritual'],
    challenges: ['Extreme Pressure', 'Self-Limiting', 'Overwhelmed by Responsibility', 'Perfectionism'],
    talents: ['Healing', 'Integration', 'Balance', 'Transformation', 'Mastery', 'Wholeness'],
    shadowAspects: ['Pressure', 'Self-Limitation', 'Overwhelm', 'Perfectionism'],
    
    lifePurpose: 'Represents integration, healing, and wholeness. Embodies the balance of all aspects - material and spiritual.',
    lifeLessons: [
      'Integrating material and spiritual',
      'Healing through balance',
      'Understanding that wholeness includes all parts',
      'Transforming through integration',
      'Mastering both inner and outer worlds'
    ],
    
    compatibleNumbers: [4, 8, 22],
    challengingNumbers: [5, 7],
    relationshipDynamics: {
      4: 'Foundation for healing',
      8: 'Material-spiritual balance',
      22: 'Building through healing',
      44: 'Maximum integration, but needs release'
    },
    
    careerPaths: [
      'Master Healer', 'Integrative Therapist', 'Holistic Practitioner', 'Balanced Leader',
      'Transformational Guide', 'Wholeness Teacher', 'Integration Specialist'
    ],
    vocationalCalling: 'Represents healing and the creation of wholeness through integration and balance.',
    
    color: 'Bronze',
    element: 'Earth',
    planet: 'Saturn',
    astrologicalCorrespondence: 'Capricorn, Virgo',
    chakra: 'All Chakras (Balanced)',
    frequency: 528, // Healing and transformation
    
    symbols: ['Four Pillars', 'Balance', 'Integration', 'Wholeness', 'Circle'],
    shapeDefinitions: {
      'Four Pillars': {
        geometricForm: 'Four parallel vertical lines (pillars, 4 vertical rectangles in square formation)',
        description: 'Four parallel vertical forms arranged in a square - the geometric expression of complete foundation and balance. Four pillars that create perfect stability.',
        significance: 'Represents the four-fold foundation of master number 44, showing how all aspects are balanced. The four pillars show how 44 creates complete stability. The square formation emphasizes the perfect balance and integration of 44.'
      },
      'Balance': {
        geometricForm: 'Symmetrical form (scales, equal parts, or balanced structure)',
        description: 'A symmetrical form with equal parts - the geometric expression of perfect balance and harmony.',
        significance: 'Represents perfect balance, harmony, and the integrated expression of 44. The balance shape shows how 44 creates harmony through integration. The symmetrical form emphasizes the balanced, whole nature of 44.'
      },
      'Integration': {
        geometricForm: 'Interconnected forms (unified structure, multiple parts as one)',
        description: 'Multiple forms interconnected as one - the geometric expression of integration and wholeness.',
        significance: 'Represents integration, wholeness, and the unified expression of 44. The integration shape shows how 44 creates wholeness through balance. The interconnected form emphasizes the complete, integrated nature of 44.'
      },
      'Wholeness': {
        geometricForm: 'Complete circle or unified form (no gaps, perfect unity)',
        description: 'A complete, unified form with no gaps - the geometric expression of wholeness and completeness.',
        significance: 'Represents wholeness, completeness, and the perfect expression of 44. The wholeness shape shows how 44 creates complete integration. The unified form emphasizes the whole, complete nature of 44.'
      },
      'Circle': {
        geometricForm: 'Circle (infinite vertices, perfect unity)',
        description: 'A perfect circle with no beginning or end - the geometric expression of complete unity and wholeness.',
        significance: 'Represents complete unity, wholeness, and the perfect expression of 44. The circle is the geometric expression of 44 - the form that creates complete integration. The infinite nature emphasizes the whole, balanced, and integrated nature of 44.'
      }
    },
    mythologicalFigures: ['Asclepius', 'Healing Masters', 'Integrated Teachers'],
    tarotCorrespondence: 'The World',
    gemstones: ['Bronze', 'Hematite', 'Black Obsidian', 'Smoky Quartz'],
    flowers: ['All Flowers in Balance', 'Composite Flowers'],
    
    favorableMonths: [4, 8],
    favorableDays: [4, 8, 13, 17, 22, 26, 31],
    peakYears: [44, 53, 62, 71]
  }
};

// ============================================================================
// CORE ARCHETYPE DEFINITIONS FROM "NUMBERS ARE A STORY"
// These provide the foundational archetypal meanings for each digit.
// For multi-digit numbers, these are layered and combined in all arrangements.
// ============================================================================

export interface DigitArchetype {
  primary: string;
  secondary: string;
  tertiary?: string; // For numbers like 2 that have three terms
  // All cultural terms from all traditions are now absorbed as equal core terms
  // They will be merged into a unified coreTerms array for equal treatment
  coreTerms: string[]; // All core terms: baseline (primary/secondary/tertiary) + cultural terms merged equally
}

/**
 * Extract all unique cultural terms from a number's cultural archetypes
 * Combines terms from symbolism and associations arrays across all traditions
 */
function extractCulturalTerms(digit: number): string[] {
  const archetype = NUMBER_ARCHETYPES[digit];
  if (!archetype || !archetype.culturalArchetypes) {
    return [];
  }

  const allTerms: Set<string> = new Set();

  archetype.culturalArchetypes.forEach(cultural => {
    // Add all symbolism terms
    if (cultural.symbolism) {
      cultural.symbolism.forEach(term => {
        if (term && term.trim()) {
          // Convert to uppercase for consistency
          allTerms.add(term.trim().toUpperCase());
        }
      });
    }

    // Add all association terms
    if (cultural.associations) {
      cultural.associations.forEach(term => {
        if (term && term.trim()) {
          allTerms.add(term.trim().toUpperCase());
        }
      });
    }
  });

  // Convert Set to sorted array for consistency
  return Array.from(allTerms).sort();
}

export const CORE_DIGIT_ARCHETYPES_BASE: Record<number, DigitArchetype> = {
  0: { primary: 'TRUTH', secondary: 'CHAOS', coreTerms: ['TRUTH', 'CHAOS'] },
  1: { primary: 'CHANGE', secondary: 'LIGHT', coreTerms: ['CHANGE', 'LIGHT'] },
  2: { primary: 'BALANCE', secondary: 'UNITY', tertiary: 'POLARITY', coreTerms: ['BALANCE', 'UNITY', 'POLARITY'] },
  3: { primary: 'DREAM', secondary: 'STRUCTURE', coreTerms: ['DREAM', 'STRUCTURE'] },
  4: { primary: 'FAITH', secondary: 'HARMONY', coreTerms: ['FAITH', 'HARMONY'] },
  5: { primary: 'GRATITUDE', secondary: 'RA', coreTerms: ['GRATITUDE', 'RA'] },
  6: { primary: 'RELEASE', secondary: 'BREATH', coreTerms: ['RELEASE', 'BREATH'] },
  7: { primary: 'AWAKENING', secondary: 'EXPANSION', coreTerms: ['AWAKENING', 'EXPANSION'] },
  8: { primary: 'SUCCESS', secondary: 'LAYERED', coreTerms: ['SUCCESS', 'LAYERED'] },
  9: { primary: 'WISDOM', secondary: 'SHARED', coreTerms: ['WISDOM', 'SHARED'] }
};

// ============================================================================
// CORE ARCHETYPE DEFINITIONS FOR DIGITS 0–9
// These provide a concise, high-level resonance for quick reference and UI.
// Combines baseline archetypes with all cultural terms from all traditions.
// ============================================================================

/**
 * Merge cultural terms into existing core terms
 * All terms are treated as equal core archetype terms
 */
function mergeCulturalTermsIntoCore(baseCoreTerms: string[], culturalTerms: string[]): string[] {
  // Start with existing core terms (baseline)
  const unifiedCoreTerms: string[] = [...baseCoreTerms];
  // Absorb all cultural terms as equal core terms
  unifiedCoreTerms.push(...culturalTerms);
  // Remove duplicates while preserving order (baseline first, then cultural)
  return Array.from(new Set(unifiedCoreTerms));
}

export const CORE_DIGIT_ARCHETYPES: Record<number, DigitArchetype> = {
  0: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[0],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[0].coreTerms, extractCulturalTerms(0))
  },
  1: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[1],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[1].coreTerms, extractCulturalTerms(1))
  },
  2: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[2],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[2].coreTerms, extractCulturalTerms(2))
  },
  3: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[3],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[3].coreTerms, extractCulturalTerms(3))
  },
  4: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[4],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[4].coreTerms, extractCulturalTerms(4))
  },
  5: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[5],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[5].coreTerms, extractCulturalTerms(5))
  },
  6: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[6],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[6].coreTerms, extractCulturalTerms(6))
  },
  7: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[7],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[7].coreTerms, extractCulturalTerms(7))
  },
  8: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[8],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[8].coreTerms, extractCulturalTerms(8))
  },
  9: { 
    ...CORE_DIGIT_ARCHETYPES_BASE[9],
    coreTerms: mergeCulturalTermsIntoCore(CORE_DIGIT_ARCHETYPES_BASE[9].coreTerms, extractCulturalTerms(9))
  }
};

// ============================================================================
// SPECIAL MULTI-DIGIT ARCHETYPES
// These are pre-defined combinations for specific numbers.
// ============================================================================

export const MULTI_DIGIT_ARCHETYPES: Record<number, DigitArchetype> = {
  10: { 
    primary: 'TRUTH OF CHANGE', 
    secondary: 'CHAOS OF LIGHT',
    coreTerms: ['TRUTH OF CHANGE', 'CHAOS OF LIGHT']
  }
};

// ============================================================================
// ARCHETYPE CONNECTIONS & RELATIONSHIPS
// ============================================================================

export interface ArchetypeConnection {
  number1: number;
  number2: number;
  relationship: 'Harmonious' | 'Challenging' | 'Neutral' | 'Transformative';
  description: string;
  advice: string;
  commonGround?: string;
  conflictAreas?: string[];
}

export const ARCHETYPE_CONNECTIONS: ArchetypeConnection[] = [
  {
    number1: 1,
    number2: 1,
    relationship: 'Challenging',
    description: 'Two independent forces can create powerful synergy or constant conflict.',
    advice: 'Learn to take turns leading, share power, and celebrate each other\'s achievements.',
    conflictAreas: ['Control', 'Recognition', 'Independence']
  },
  {
    number1: 1,
    number2: 2,
    relationship: 'Transformative',
    description: 'The leader finds support; the supporter finds strength. Yin and yang in perfect balance.',
    advice: '1 provides direction; 2 provides harmony. Balance action with reflection.',
    commonGround: 'Both seek to create and manifest',
    conflictAreas: ['Independence vs. Partnership']
  },
  {
    number1: 1,
    number2: 3,
    relationship: 'Harmonious',
    description: 'Creative leadership and dynamic expression create an inspiring partnership.',
    advice: '1 initiates; 3 expresses. Together they create innovative and exciting projects.',
    commonGround: 'Both are independent and creative'
  },
  {
    number1: 1,
    number2: 4,
    relationship: 'Transformative',
    description: 'Freedom vs. Structure creates tension, but together they build lasting foundations.',
    advice: '1 brings innovation; 4 provides stability. Compromise is essential.',
    conflictAreas: ['Freedom vs. Structure', 'Change vs. Stability']
  },
  {
    number1: 1,
    number2: 5,
    relationship: 'Harmonious',
    description: 'Adventure and leadership create an exciting, dynamic partnership full of possibilities.',
    advice: 'Both love freedom and adventure. Support each other\'s independence while building together.',
    commonGround: 'Freedom, adventure, and independence'
  },
  {
    number1: 2,
    number2: 3,
    relationship: 'Harmonious',
    description: 'Emotional sensitivity meets creative expression in beautiful harmony.',
    advice: '2 provides emotional depth; 3 provides creative expression. Perfect for artistic partnerships.',
    commonGround: 'Both value beauty, harmony, and expression'
  },
  {
    number1: 2,
    number2: 4,
    relationship: 'Harmonious',
    description: 'Stability and harmony create the perfect foundation for home and family.',
    advice: 'Both value security and stability. Build a solid, harmonious life together.',
    commonGround: 'Security, stability, and foundation'
  },
  {
    number1: 3,
    number2: 3,
    relationship: 'Harmonious',
    description: 'Triple creativity and joy, but may need grounding to manifest ideas.',
    advice: 'Creativity multiplies, but add structure to bring ideas to fruition.',
    commonGround: 'Creativity, expression, and joy'
  },
  {
    number1: 6,
    number2: 6,
    relationship: 'Harmonious',
    description: 'Doubled nurturing creates a loving, harmonious home, but watch for codependency.',
    advice: 'Perfect for family and home, but maintain individual identity within partnership.',
    commonGround: 'Love, service, and harmony',
    conflictAreas: ['Boundaries', 'Individual Identity']
  },
  {
    number1: 7,
    number2: 7,
    relationship: 'Neutral',
    description: 'Deep spiritual connection, but risk of isolation from the world.',
    advice: 'Share wisdom and spiritual insights, but also engage with the material world together.',
    commonGround: 'Spirituality, wisdom, and inner quest'
  },
  {
    number1: 8,
    number2: 8,
    relationship: 'Challenging',
    description: 'Doubled power can create great success or constant power struggles.',
    advice: 'Use power together for mutual benefit and service to others, not against each other.',
    conflictAreas: ['Control', 'Power', 'Resources']
  },
  {
    number1: 9,
    number2: 9,
    relationship: 'Harmonious',
    description: 'Maximum compassion and service, perfect for humanitarian partnerships.',
    advice: 'Work together to serve humanity, but also take care of your own needs.',
    commonGround: 'Service, compassion, and humanitarian work',
    conflictAreas: ['Self-Care', 'Practical Matters']
  },
  {
    number1: 11,
    number2: 22,
    relationship: 'Harmonious',
    description: 'Master illuminator meets master builder - vision and practical manifestation.',
    advice: '11 provides vision and inspiration; 22 builds the practical structure. Perfect combination.',
    commonGround: 'Both are master numbers serving humanity'
  },
  {
    number1: 11,
    number2: 33,
    relationship: 'Harmonious',
    description: 'Perfect teacher-student or co-teacher dynamic. Spiritual illumination with compassionate service.',
    advice: '11 inspires; 33 teaches with love. Together they create powerful transformation.',
    commonGround: 'Spiritual service and teaching'
  },
  {
    number1: 22,
    number2: 33,
    relationship: 'Harmonious',
    description: 'Building through teaching and service. Creating structures that serve humanity.',
    advice: '22 builds the foundation; 33 fills it with love and teaching. Perfect partnership.',
    commonGround: 'Service to humanity through different expressions'
  }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get archetype for a number
 */
export function getArchetype(number: number): NumberArchetype {
  return NUMBER_ARCHETYPES[number] || NUMBER_ARCHETYPES[1];
}

/**
 * Get archetype with custom terms included in keywords
 * This ensures custom user-defined terms appear alongside default keywords
 */
export function getArchetypeWithCustomTerms(number: number): NumberArchetype {
  const archetype = getArchetype(number);
  
  // Get custom terms for this number
  try {
    const stored = localStorage.getItem('fr00t_custom_terms');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.data && parsed.data[number] && Array.isArray(parsed.data[number])) {
        const customTerms = parsed.data[number];
        // Merge custom terms with keywords (avoid duplicates, case-insensitive)
        // Custom terms should appear FIRST
        const defaultKeywords = archetype.keywords || [];
        const defaultKeywordsUpper = defaultKeywords.map(k => k.toUpperCase());
        const customTermsFiltered = customTerms.filter((term: string) => 
          typeof term === 'string' && !defaultKeywordsUpper.includes(term.toUpperCase())
        );
        
        // Put custom terms first, then default keywords
        const mergedKeywords = [...customTermsFiltered, ...defaultKeywords];
        
        // Return archetype with merged keywords
        return {
          ...archetype,
          keywords: mergedKeywords
        };
      }
    }
  } catch (error) {
    // Silently fail and return default archetype
  }
  
  return archetype;
}

/**
 * Get digit archetype for a single digit (0-9)
 */
export function getDigitArchetype(digit: number): DigitArchetype | null {
  if (digit >= 0 && digit <= 9) {
    return CORE_DIGIT_ARCHETYPES[digit];
  }
  return null;
}

/**
 * Get all available terms for a digit
 * All terms (baseline + cultural) are now unified as equal core terms
 * Includes custom user-defined terms if available
 */
function getAllTermsForDigit(arch: DigitArchetype, number?: number): string[] {
  // All terms are now unified in coreTerms and treated equally
  const defaultTerms = arch.coreTerms || [];
  
  // Include custom terms if number is provided
  if (number !== undefined) {
    try {
      // Direct localStorage access to avoid circular dependencies
      const stored = localStorage.getItem('fr00t_custom_terms');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.data && parsed.data[number] && Array.isArray(parsed.data[number])) {
          const customTerms = parsed.data[number];
          // Merge custom terms (avoid duplicates, case-insensitive)
          // Custom terms should appear FIRST
          const defaultUpper = defaultTerms.map(t => t.toUpperCase());
          const customTermsFiltered = customTerms.filter((term: string) => 
            typeof term === 'string' && !defaultUpper.includes(term.toUpperCase())
          );
          
          // Put custom terms first, then default terms
          return [...customTermsFiltered, ...defaultTerms];
        }
      }
    } catch (error) {
      // If custom terms storage is not available, just return default terms
      // Silently continue - this is expected if localStorage is not available
    }
  }
  
  return defaultTerms;
}

/**
 * Break down a number into its individual digits
 */
export function getDigits(number: number): number[] {
  return String(number)
    .split('')
    .map(d => parseInt(d, 10))
    .filter(d => !isNaN(d));
}

/**
 * Generate all arrangements of layered archetypes for a multi-digit number
 * Explores all combinations of primary, secondary, and tertiary terms
 * Now includes term-to-digit mapping for individual term coloring
 */
export function getLayeredArchetypes(number: number): {
  archetypes: string[];
  combinations: Array<{
    arrangement: string;
    terms: string[];
    termDigits?: Record<string, number>; // Maps each term to its source digit
  }>;
} {
  // Check for pre-defined multi-digit archetype first
  if (MULTI_DIGIT_ARCHETYPES[number]) {
    const arch = MULTI_DIGIT_ARCHETYPES[number];
    const terms: string[] = [arch.primary, arch.secondary, arch.tertiary].filter((t): t is string => Boolean(t));
    const digits = getDigits(number);
    // For multi-digit, assign terms to first digit (or create mapping if needed)
    const termDigits: Record<string, number> = {};
    terms.forEach(term => {
      termDigits[term] = digits[0] || number;
    });
    return {
      archetypes: [terms.join(' ▪ ')],
      combinations: [{
        arrangement: terms.join(' ▪ '),
        terms,
        termDigits
      }]
    };
  }

  const digits = getDigits(number);
  
  // If single digit, return its archetype with all unified core terms
  // All terms (baseline + cultural) are now treated as equal core terms
  if (digits.length === 1) {
    const digit = digits[0];
    const arch = getDigitArchetype(digit);
    if (!arch) {
      return { archetypes: [], combinations: [] };
    }
    
    // All terms are unified in coreTerms - treat them equally
    const allCoreTerms = arch.coreTerms;
    
    // Create term-to-digit mapping (all terms belong to same digit)
    const termDigits: Record<string, number> = {};
    allCoreTerms.forEach(term => {
      termDigits[term] = digit;
    });
    
    // Primary arrangement: all core terms together
    const combinations: Array<{ arrangement: string; terms: string[]; termDigits: Record<string, number> }> = [{
      arrangement: allCoreTerms.join(' ▪ '),
      terms: allCoreTerms,
      termDigits
    }];
    
    // Generate combinations using all core terms equally
    // Create pairs and triples from all terms (baseline + cultural treated equally)
    if (allCoreTerms.length >= 2) {
      const selectedTerms = allCoreTerms.slice(0, Math.min(4, allCoreTerms.length));
      const selectedTermDigits: Record<string, number> = {};
      selectedTerms.forEach(term => {
        selectedTermDigits[term] = digit;
      });
      // Primary + first few other core terms
      combinations.push({
        arrangement: selectedTerms.join(' ▪ '),
        terms: selectedTerms,
        termDigits: selectedTermDigits
      });
    }
    
    return {
      archetypes: combinations.map(c => c.arrangement),
      combinations
    };
  }

  // For multi-digit numbers, get archetypes for each digit
  const digitArchetypes = digits
    .map(d => getDigitArchetype(d))
    .filter((arch): arch is DigitArchetype => arch !== null);

  if (digitArchetypes.length === 0) {
    return { archetypes: [], combinations: [] };
  }

  // Helper function to create term-to-digit mapping
  const createTermDigitsMapping = (terms: string[], digitIndex: number): Record<string, number> => {
    const mapping: Record<string, number> = {};
    terms.forEach(term => {
      mapping[term] = digits[digitIndex];
    });
    return mapping;
  };

  // Generate all combinations with term-to-digit mappings
  const combinations: Array<{ arrangement: string; terms: string[]; termDigits: Record<string, number> }> = [];
  
  // Extract all terms from all digits (baseline + cultural + custom)
  const allTerms: string[] = [];
  digitArchetypes.forEach((arch, index) => {
    const digit = digits[index];
    const terms = getAllTermsForDigit(arch, digit);
    allTerms.push(...terms);
  });

  // Generate arrangements using all unified core terms equally
  // All terms (baseline + cultural) are now absorbed as equal core terms
  
  // 1. All core terms from all digits combined (baseline + cultural treated equally)
  const allCoreTermsSequential: string[] = [];
  const allCoreTermsMapping: Record<string, number> = {};
  digitArchetypes.forEach((arch, index) => {
    // All terms in coreTerms are equal - include them all
    allCoreTermsSequential.push(...arch.coreTerms);
    // Map each term to its source digit
    arch.coreTerms.forEach(term => {
      allCoreTermsMapping[term] = digits[index];
    });
  });
  combinations.push({
    arrangement: allCoreTermsSequential.join(' ▪ '),
    terms: allCoreTermsSequential,
    termDigits: allCoreTermsMapping
  });

  // 2. Core terms combination - first few from each digit (all terms equal)
  const coreTermsSelection: string[] = [];
  const coreTermsSelectionMapping: Record<string, number> = {};
  digitArchetypes.forEach((arch, index) => {
    // Take first 3-4 core terms from each digit (includes baseline + cultural equally)
    const termsToInclude = arch.coreTerms.slice(0, Math.min(4, arch.coreTerms.length));
    coreTermsSelection.push(...termsToInclude);
    termsToInclude.forEach(term => {
      coreTermsSelectionMapping[term] = digits[index];
    });
  });
  if (coreTermsSelection.length > 0) {
    combinations.push({
      arrangement: coreTermsSelection.join(' ▪ '),
      terms: coreTermsSelection,
      termDigits: coreTermsSelectionMapping
    });
  }

  // 3. Primary core terms from each digit (first term from coreTerms - could be baseline or cultural)
  const primaryCoreTerms: string[] = [];
  const primaryCoreTermsMapping: Record<string, number> = {};
  digitArchetypes.forEach((arch, index) => {
    const term = arch.coreTerms[0] || arch.primary;
    primaryCoreTerms.push(term);
    primaryCoreTermsMapping[term] = digits[index];
  });
  if (primaryCoreTerms.length > 1) {
    combinations.push({
      arrangement: primaryCoreTerms.join(' ▪ '),
      terms: primaryCoreTerms,
      termDigits: primaryCoreTermsMapping
    });
  }

  // 4. Second core term from each digit (all terms equal in coreTerms)
  const secondCoreTerms: string[] = [];
  const secondCoreTermsMapping: Record<string, number> = {};
  digitArchetypes.forEach((arch, index) => {
    if (arch.coreTerms[1]) {
      secondCoreTerms.push(arch.coreTerms[1]);
      secondCoreTermsMapping[arch.coreTerms[1]] = digits[index];
    }
  });
  if (secondCoreTerms.length === digitArchetypes.length && secondCoreTerms.length > 1) {
    combinations.push({
      arrangement: secondCoreTerms.join(' ▪ '),
      terms: secondCoreTerms,
      termDigits: secondCoreTermsMapping
    });
  }

  // 5. Cross-combinations for two-digit numbers: explore all arrangements
  // All core terms are equal - mix them freely
  if (digitArchetypes.length === 2) {
    const [first, second] = digitArchetypes;
    const firstDigit = digits[0];
    const secondDigit = digits[1];
    
    // All core terms from each digit (baseline + cultural unified)
    const firstCoreTerms = first.coreTerms;
    const secondCoreTerms = second.coreTerms;
    
    // Generate combinations using all core terms equally
    // Use first few core terms from each to avoid overwhelming combinations
    const firstKeyTerms = firstCoreTerms.slice(0, Math.min(5, firstCoreTerms.length));
    const secondKeyTerms = secondCoreTerms.slice(0, Math.min(5, secondCoreTerms.length));
    
    // Cross combinations - all terms treated equally
    firstKeyTerms.forEach(firstTerm => {
      secondKeyTerms.forEach(secondTerm => {
        const termDigits: Record<string, number> = {
          [firstTerm]: firstDigit,
          [secondTerm]: secondDigit
        };
        combinations.push({
          arrangement: `${firstTerm} OF ${secondTerm}`,
          terms: [firstTerm, secondTerm],
          termDigits
        });
        combinations.push({
          arrangement: `${firstTerm} WITH ${secondTerm}`,
          terms: [firstTerm, secondTerm],
          termDigits
        });
      });
    });
  }
  
  // 6. For 3+ digit numbers, create layered combinations using all core terms equally
  if (digitArchetypes.length >= 3) {
    // First core term from each digit (all terms equal)
    const firstCoreLayered: string[] = [];
    const firstCoreLayeredMapping: Record<string, number> = {};
    digitArchetypes.forEach((arch, index) => {
      const term = arch.coreTerms[0] || arch.primary;
      firstCoreLayered.push(term);
      firstCoreLayeredMapping[term] = digits[index];
    });
    combinations.push({
      arrangement: firstCoreLayered.join(' ▪ '),
      terms: firstCoreLayered,
      termDigits: firstCoreLayeredMapping
    });
    
    // Create "OF" chains using first core term from each digit
    const ofChainTerms: string[] = [];
    const ofChainMapping: Record<string, number> = {};
    digitArchetypes.forEach((arch, index) => {
      const term = arch.coreTerms[0] || arch.primary;
      ofChainTerms.push(term);
      ofChainMapping[term] = digits[index];
    });
    const ofChain = ofChainTerms.reduce((acc, term, index) => {
      if (index === 0) return term;
      return `${acc} OF ${term}`;
    }, '');
    combinations.push({
      arrangement: ofChain,
      terms: ofChainTerms,
      termDigits: ofChainMapping
    });
    
    // Mixed: use different core terms from each digit (all terms equal)
    const mixedCoreTerms: string[] = [];
    const mixedCoreTermsMapping: Record<string, number> = {};
    digitArchetypes.forEach((arch, index) => {
      // Use first core term for outer digits, second for middle digits (all equal)
      const termIndex = (index === 0 || index === digitArchetypes.length - 1) ? 0 : 1;
      const term = arch.coreTerms[termIndex] || arch.coreTerms[0] || arch.primary;
      mixedCoreTerms.push(term);
      mixedCoreTermsMapping[term] = digits[index];
    });
    combinations.push({
      arrangement: mixedCoreTerms.join(' ▪ '),
      terms: mixedCoreTerms,
      termDigits: mixedCoreTermsMapping
    });
  }


  // Remove duplicates
  const uniqueCombinations = combinations.filter((combo, index, self) =>
    index === self.findIndex(c => c.arrangement === combo.arrangement)
  );

  const archetypes = uniqueCombinations.map(c => c.arrangement);

  return {
    archetypes,
    combinations: uniqueCombinations
  };
}

/**
 * Get connection between two numbers
 */
export function getConnection(number1: number, number2: number): ArchetypeConnection | null {
  const connection = ARCHETYPE_CONNECTIONS.find(
    c => (c.number1 === number1 && c.number2 === number2) ||
         (c.number1 === number2 && c.number2 === number1)
  );
  return connection || null;
}

/**
 * Get all compatible numbers for a given number
 */
export function getCompatibleNumbers(number: number): number[] {
  const archetype = getArchetype(number);
  return archetype.compatibleNumbers;
}

/**
 * Get all challenging numbers for a given number
 */
export function getChallengingNumbers(number: number): number[] {
  const archetype = getArchetype(number);
  return archetype.challengingNumbers;
}

/**
 * Get relationship dynamics between two numbers
 */
export function getRelationshipDynamic(number1: number, number2: number): string {
  const archetype1 = getArchetype(number1);
  return archetype1.relationshipDynamics[number2] || 'Unique dynamic - explore and learn together.';
}

/**
 * Get all cultural archetype mappings for a number
 */
export function getCulturalArchetypes(number: number): CulturalArchetypeMapping[] {
  const archetype = getArchetype(number);
  return archetype.culturalArchetypes || [];
}

/**
 * Get cultural archetype mappings for a specific tradition
 */
export function getCulturalArchetypeByTradition(
  number: number, 
  tradition: string
): CulturalArchetypeMapping | null {
  const culturalArchetypes = getCulturalArchetypes(number);
  return culturalArchetypes.find(ca => 
    ca.tradition.toLowerCase() === tradition.toLowerCase()
  ) || null;
}

/**
 * Get all cultural archetypes by lucky status
 */
export function getCulturalArchetypesByLuckyStatus(
  number: number,
  status: 'lucky' | 'unlucky' | 'neutral' | 'sacred'
): CulturalArchetypeMapping[] {
  const culturalArchetypes = getCulturalArchetypes(number);
  return culturalArchetypes.filter(ca => ca.luckyStatus === status);
}

/**
 * Get all available traditions for a number
 */
export function getAvailableTraditions(number: number): string[] {
  const culturalArchetypes = getCulturalArchetypes(number);
  return culturalArchetypes.map(ca => ca.tradition);
}

/**
 * Get all cultural meanings for a number across all traditions
 */
export function getAllCulturalMeanings(number: number): string[] {
  const culturalArchetypes = getCulturalArchetypes(number);
  return culturalArchetypes.map(ca => ca.meaning);
}

/**
 * Search cultural archetypes by keyword across all traditions
 */
export function searchCulturalArchetypes(
  number: number,
  keyword: string
): CulturalArchetypeMapping[] {
  const culturalArchetypes = getCulturalArchetypes(number);
  const lowerKeyword = keyword.toLowerCase();
  return culturalArchetypes.filter(ca =>
    ca.meaning.toLowerCase().includes(lowerKeyword) ||
    ca.tradition.toLowerCase().includes(lowerKeyword) ||
    ca.symbolism.some(s => s.toLowerCase().includes(lowerKeyword)) ||
    (ca.associations && ca.associations.some(a => a.toLowerCase().includes(lowerKeyword))) ||
    (ca.notes && ca.notes.toLowerCase().includes(lowerKeyword))
  );
}

